import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { SundayClosuresCard } from '@/components/SundayClosuresCard';
import { useAuth } from '@/context/AuthContext';
import { FiBook, FiUsers, FiClock, FiSearch, FiPlus, FiUpload, FiCheckCircle, FiXCircle, FiImage, FiEdit, FiX, FiPhone, FiMail, FiStar, FiMessageSquare, FiDownload, FiUserPlus, FiCalendar, FiHeart, FiBarChart2, FiAlertTriangle, FiAlertCircle, FiDollarSign, FiTrendingUp, FiTrendingDown, FiUserMinus, FiPackage, FiGift, FiList, FiUser, FiPause } from 'react-icons/fi';
import { Calendar } from '@/components/ui/calendar';
import StatsTab from '@/components/admin/StatsTab';
import AdminQuickActions from '@/components/admin/AdminQuickActions';
import AddBookTab from '@/components/admin/AddBookTab';
import PauseRequestsTab from '@/components/admin/PauseRequestsTab';
import BookSuggestionsTab from '@/components/admin/BookSuggestionsTab';
import GiftsTab from '@/components/admin/GiftsTab';
import BatchDeliveryModal from '@/components/admin/BatchDeliveryModal';
import PaymentHistoryPanel from '@/components/admin/PaymentHistoryPanel';
import DeliveryBalancePanel from '@/components/admin/DeliveryBalancePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('holds');
  const [isbn, setIsbn] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [allBorrows, setAllBorrows] = useState([]);
  const [allHolds, setAllHolds] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // User details modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  
  // Book library card modal
  const [bookLibraryCard, setBookLibraryCard] = useState(null);
  const [loadingBookCard, setLoadingBookCard] = useState(false);
  
  // User subscription filter
  const [userSubscriptionFilter, setUserSubscriptionFilter] = useState('all'); // 'all', 'active', 'inactive'
  
  // Subscription update modal
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionUpdate, setSubscriptionUpdate] = useState({
    status: 'active',
    plan: 'cash',
    duration: 1,
    note: ''
  });
  const [updatingSubscription, setUpdatingSubscription] = useState(false);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Batch delivery modal
  const [deliveryModalHold, setDeliveryModalHold] = useState(null);

  // Toggle book visibility inside the book library-card modal
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  
  // Fines management
  const [allFines, setAllFines] = useState([]);
  const [finesSummary, setFinesSummary] = useState(null);
  const [loadingFines, setLoadingFines] = useState(false);
  const [fineFilter, setFineFilter] = useState('unpaid'); // 'all', 'unpaid', 'paid', 'waived'
  const [adjustingFine, setAdjustingFine] = useState(null);
  const [selectedFineIds, setSelectedFineIds] = useState([]);
  const [bulkActing, setBulkActing] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  
  // Hold rejection audit
  const [holdRejections, setHoldRejections] = useState(null);
  const [loadingRejections, setLoadingRejections] = useState(false);
  const [rejectionWindowDays, setRejectionWindowDays] = useState(30);

  // Stuck rental orders (global view inside Rejections tab)
  const [stuckRentals, setStuckRentals] = useState({ total: 0, rentals: [] });
  const [loadingStuckRentals, setLoadingStuckRentals] = useState(false);
  const [clearingStuckId, setClearingStuckId] = useState(null);
  
  // Add-ons management
  const [addons, setAddons] = useState([]);
  const [addonTemplates, setAddonTemplates] = useState({});
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [showCreateAddon, setShowCreateAddon] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [newAddon, setNewAddon] = useState({
    name: '',
    description: '',
    base_price: 0,
    addon_type: 'workshop',
    billing_frequency: 'one-time',
    image_url: '',
    event_datetime: '',
    event_end_datetime: '',
    location: '',
    payment_mode: 'one_time',
    max_capacity: null,
    allow_non_members: false,
    non_member_price: null,
    attendance_policy: 'reservation',
    allow_free_rsvp: false,
    external_registration_url: '',
    ticket_categories: [],
    features: [],
    visible_to_members: true
  });
  const [newFeature, setNewFeature] = useState('');
  const [uploadingAddonImage, setUploadingAddonImage] = useState(false);
  const [editingAddonId, setEditingAddonId] = useState(null); // null = create mode, addon.id = edit mode
  const [rsvpAddon, setRsvpAddon] = useState(null);
  const [rsvpData, setRsvpData] = useState(null);
  const [loadingRsvp, setLoadingRsvp] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [showAddCashSubscriber, setShowAddCashSubscriber] = useState(false);
  const [cashSubMode, setCashSubMode] = useState('existing'); // 'existing' | 'walkin'
  const [cashSubUserQuery, setCashSubUserQuery] = useState('');
  const [cashSubUserResults, setCashSubUserResults] = useState([]);
  const [cashSubSelectedUser, setCashSubSelectedUser] = useState(null);
  const [cashSubWalkin, setCashSubWalkin] = useState({ name: '', email: '', phone: '' });
  const [cashSubAmount, setCashSubAmount] = useState(0);
  const [cashSubPaymentMethod, setCashSubPaymentMethod] = useState('cash');
  const [cashSubNotes, setCashSubNotes] = useState('');
  const [cashSubGuests, setCashSubGuests] = useState([]); // [{name, email, phone, is_member}]
  const [cashSubCategoryId, setCashSubCategoryId] = useState('');
  const [savingCashSub, setSavingCashSub] = useState(false);
  const [removingSubId, setRemovingSubId] = useState(null);
  
  // Email sending state
  const [sendingRenewalReminders, setSendingRenewalReminders] = useState(false);
  const [sendingExpiredNotices, setSendingExpiredNotices] = useState(false);
  
  // Subscription adjustment state
  const [adjustingSubscription, setAdjustingSubscription] = useState(false);
  const [newSubscriptionEndDate, setNewSubscriptionEndDate] = useState('');

  // Edit member details state
  const [editingMember, setEditingMember] = useState(false);
  const [editMemberData, setEditMemberData] = useState({ name: '', email: '', phone: '' });
  const [savingMember, setSavingMember] = useState(false);
  const [fixingStaff, setFixingStaff] = useState(false);
  const [recalculatingPayments, setRecalculatingPayments] = useState(false);
  
  // Hold status filter
  const [holdStatusFilter, setHoldStatusFilter] = useState('all');
  
  // Edit hold modal
  const [editingHold, setEditingHold] = useState(null);
  const [editHoldData, setEditHoldData] = useState({
    preferred_date: '',
    delivery_option: 'self_pickup'
  });
  const [savingHold, setSavingHold] = useState(false);
  
  // Reviews moderation
  const [pendingReviews, setPendingReviews] = useState([]);
  const [processingReview, setProcessingReview] = useState(null);
  
  // Add book mode (isbn or manual)
  const [addBookMode, setAddBookMode] = useState('isbn');

  // Borrow rules state
  const [borrowRules, setBorrowRules] = useState([]);
  const [loadingBorrowRules, setLoadingBorrowRules] = useState(false);
  const [newRuleCategory, setNewRuleCategory] = useState('');
  const [newRuleDays, setNewRuleDays] = useState('');
  const [addingRule, setAddingRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editRuleDays, setEditRuleDays] = useState('');
  
  // Manual book entry state
  const [manualBook, setManualBook] = useState({
    title: '', authors: '', isbn: '', publisher: '', published_date: '',
    description: '', categories: '', cover_image_url: '', total_copies: 1, available_from: '',
    // Procurement fields
    procurement_type: 'purchased', purchase_price: '', vendor: '', procurement_date: '', procurement_notes: ''
  });
  const [addingBook, setAddingBook] = useState(false);
  const [bookResult, setBookResult] = useState(null);

  // Authors/Categories dropdown state
  const [allAuthors, setAllAuthors] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [authorInput, setAuthorInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const authorDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Vendor/Donor dropdown state
  const [allVendors, setAllVendors] = useState([]);
  const [vendorInput, setVendorInput] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const vendorDropdownRef = useRef(null);
  
  // Manual history entry state
  const [historyEntry, setHistoryEntry] = useState({
    user_email: '', book_title: '', borrow_date: '', return_date: ''
  });
  const [addingHistory, setAddingHistory] = useState(false);
  const [historyResult, setHistoryResult] = useState(null);
  const [bookSearchResults, setBookSearchResults] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  
  // Procurements state
  const [procurements, setProcurements] = useState({ books: [], summary: {} });
  const [loadingProcurements, setLoadingProcurements] = useState(false);
  
  // Backup state
  const [backupInfo, setBackupInfo] = useState(null);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(null);

  // Member Actions state
  const [memberActionEmail, setMemberActionEmail] = useState('');
  const [memberActionBookSearch, setMemberActionBookSearch] = useState('');
  const [memberActionBookResults, setMemberActionBookResults] = useState([]);
  const [selectedBooksForAction, setSelectedBooksForAction] = useState([]);
  const [searchingMemberBooks, setSearchingMemberBooks] = useState(false);
  const [processingMemberAction, setProcessingMemberAction] = useState(false);
  const [memberHoldRequestedDate, setMemberHoldRequestedDate] = useState('');
  
  // Member search state (for Checkout and Add History)
  const [checkoutForChild, setCheckoutForChild] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchingMembers, setSearchingMembers] = useState(false);
  
  // History member search state
  const [historyMemberSearchQuery, setHistoryMemberSearchQuery] = useState('');
  const [historyMemberSearchResults, setHistoryMemberSearchResults] = useState([]);
  const [selectedHistoryMember, setSelectedHistoryMember] = useState(null);
  const [searchingHistoryMembers, setSearchingHistoryMembers] = useState(false);
  
  // Loan extension state
  const [extendingLoan, setExtendingLoan] = useState(null);
  const [extensionDays, setExtensionDays] = useState(7);

  // Coming Soon Calendar state
  const [calendarData, setCalendarData] = useState({ dates: {}, total_books: 0 });
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Recommendation requests state
  const [recommendationRequests, setRecommendationRequests] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [recommendationBookSearch, setRecommendationBookSearch] = useState('');
  const [recommendationBookResults, setRecommendationBookResults] = useState([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);

  useEffect(() => {
    if (activeTab === 'holds') {
      fetchAllHolds();
    } else if (activeTab === 'borrows') {
      fetchAllBorrows();
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'procurements') {
      fetchProcurements();
      // Also pull rejection data so we can surface a "books to reorder" nudge
      fetchHoldRejections(30);
    } else if (activeTab === 'reviews') {
      fetchPendingReviews();
    } else if (activeTab === 'backup') {
      fetchBackupInfo();
    } else if (activeTab === 'coming-soon-calendar') {
      fetchComingSoonCalendar();
    } else if (activeTab === 'recommendations') {
      fetchRecommendationRequests();
    } else if (activeTab === 'stats') {
      fetchDashboardStats();
      fetchBorrowRules();
    } else if (activeTab === 'addons') {
      fetchAddons();
      fetchAddonTemplates();
    } else if (activeTab === 'fines') {
      fetchFines();
      fetchFinesSummary();
    } else if (activeTab === 'rejections') {
      fetchHoldRejections(rejectionWindowDays);
      fetchStuckRentals();
    } else if (activeTab === 'addbook') {
      fetchAuthorsAndCategories();
    }
  }, [activeTab]);

  // Refresh holds list when a quick action (Today panel) changes a hold's status
  useEffect(() => {
    const onQuickAction = () => { if (activeTab === 'holds') fetchAllHolds(); };
    window.addEventListener('quick-action-done', onQuickAction);
    return () => window.removeEventListener('quick-action-done', onQuickAction);
  }, [activeTab]);

  // Scheduled pickup requests (members reserving a Thursday box slot)
  const [scheduledPickupReqs, setScheduledPickupReqs] = useState([]);
  const fetchScheduledPickupReqs = async () => {
    try {
      const res = await axios.get(`${API}/admin/pickups/scheduled`, { headers: getAuthHeader() });
      setScheduledPickupReqs(res.data || []);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { if (activeTab === 'holds') fetchScheduledPickupReqs(); }, [activeTab]);
  const handlePickupReqAction = async (pickupId, cancel) => {
    let charge = 0;
    if (!cancel) {
      const input = window.prompt('Payment amount collected for this pickup (₹). Enter 0 if none:', '0');
      if (input === null) return;
      charge = parseFloat(input);
      if (isNaN(charge) || charge < 0) { alert('Please enter a valid amount (0 or more)'); return; }
    }
    try {
      const res = await axios.put(`${API}/admin/pickups/${pickupId}/complete?cancel=${cancel}&charge_amount=${charge}`, {}, { headers: getAuthHeader() });
      if (res.data?.message) alert(res.data.message);
      fetchScheduledPickupReqs();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    }
  };

  // Staff scheduling a pickup on behalf of a member
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [pickupModalMember, setPickupModalMember] = useState(null);
  const [pickupModalDate, setPickupModalDate] = useState('');
  const [pickupModalAddress, setPickupModalAddress] = useState('');
  const [pickupModalQuery, setPickupModalQuery] = useState('');
  const [pickupModalResults, setPickupModalResults] = useState([]);
  const [schedulingAdminPickup, setSchedulingAdminPickup] = useState(false);
  const openPickupModal = (member = null) => {
    setPickupModalMember(member);
    setPickupModalQuery('');
    setPickupModalResults([]);
    setPickupModalDate('');
    setPickupModalAddress('');
    setPickupModalOpen(true);
  };
  const searchPickupMembers = async (q) => {
    setPickupModalQuery(q);
    if (!q || q.length < 2) { setPickupModalResults([]); return; }
    try {
      const res = await axios.get(`${API}/admin/search-members?q=${encodeURIComponent(q)}`, { headers: getAuthHeader() });
      setPickupModalResults(res.data || []);
    } catch { /* ignore */ }
  };
  const submitAdminPickup = async () => {
    if (!pickupModalMember) { alert('Pick a member first'); return; }
    if (!pickupModalDate) { alert('Pick a Thursday date'); return; }
    setSchedulingAdminPickup(true);
    try {
      const res = await axios.post(`${API}/admin/pickups/schedule`, { user_id: pickupModalMember.id, pickup_date: pickupModalDate, address: pickupModalAddress }, { headers: getAuthHeader() });
      alert(res.data?.message || 'Pickup scheduled');
      setPickupModalOpen(false);
      fetchScheduledPickupReqs();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to schedule pickup');
    } finally {
      setSchedulingAdminPickup(false);
    }
  };
  const fetchFines = async () => {
    setLoadingFines(true);
    try {
      const response = await axios.get(`${API}/admin/fines`, { headers: getAuthHeader() });
      setAllFines(response.data);
    } catch (error) {
      console.error('Failed to fetch fines:', error);
    } finally {
      setLoadingFines(false);
    }
  };

  const fetchFinesSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/fines/summary`, { headers: getAuthHeader() });
      setFinesSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch fines summary:', error);
    }
  };

  const fetchHoldRejections = async (days = 30) => {
    setLoadingRejections(true);
    try {
      const response = await axios.get(`${API}/admin/hold-rejections?days=${days}&limit=200`, { headers: getAuthHeader() });
      setHoldRejections(response.data);
    } catch (error) {
      console.error('Failed to fetch hold rejections:', error);
      setHoldRejections({ window_days: days, total_rejections: 0, rejections: [], by_book: [] });    } finally {
      setLoadingRejections(false);
    }
  };

  const fetchStuckRentals = async () => {
    setLoadingStuckRentals(true);
    try {
      const res = await axios.get(`${API}/admin/stuck-rentals`, { headers: getAuthHeader() });
      setStuckRentals(res.data || { total: 0, rentals: [] });
    } catch (err) {
      console.error('Failed to fetch stuck rentals:', err);
      setStuckRentals({ total: 0, rentals: [] });
    } finally {
      setLoadingStuckRentals(false);
    }
  };

  const handleClearStuckRental = async (rentalId, bookTitle, memberName) => {
    if (!window.confirm(`Cancel stuck order for "${bookTitle}" by ${memberName}?`)) return;
    setClearingStuckId(rentalId);
    try {
      await axios.delete(`${API}/admin/stuck-rentals/${rentalId}`, { headers: getAuthHeader() });
      await fetchStuckRentals();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to clear stuck rental');
    } finally {
      setClearingStuckId(null);
    }
  };

  const handleClearAllStuckRentals = async () => {
    if (!window.confirm(`Clear ALL ${stuckRentals.total} stuck rental orders? This cannot be undone.`)) return;
    setClearingStuckId('ALL');
    try {
      const res = await axios.post(`${API}/admin/stuck-rentals/clear-all`, {}, { headers: getAuthHeader() });
      alert(`Cleared ${res.data.cleared} stuck rental order(s).`);
      await fetchStuckRentals();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to clear stuck rentals');
    } finally {
      setClearingStuckId(null);
    }
  };

  const handleUndoReturn = async (hold) => {
    const bookTitle = hold.book?.title || 'this book';
    const memberName = (hold.user?.name || 'this member') + (hold.is_child_hold ? ` (${hold.child_name || 'Child'} — Children membership)` : '');
    const choice = window.prompt(
      `Undo return of "${bookTitle}" for ${memberName}?\n\nType "borrowed" or "delivered" to choose the new status.\nLeave blank to default to "borrowed".\nType "cancel" to abort.`,
      'borrowed'
    );
    if (choice === null) return;
    const target = choice.trim().toLowerCase() || 'borrowed';
    if (target === 'cancel') return;
    if (!['borrowed', 'delivered'].includes(target)) {
      alert('Please type "borrowed" or "delivered" — undo cancelled.');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/holds/${hold.id}/undo-return?target_status=${target}`,
        {},
        { headers: getAuthHeader() }
      );
      const refundMsg = res.data.refunded_late_fee > 0
        ? ` Late fee of ₹${res.data.refunded_late_fee} also reversed.`
        : '';
      alert(`Return undone. Book is back to "${target}".${refundMsg}`);
      await fetchAllHolds();
      // If we're inside the user-details modal, refresh it so the row updates immediately.
      if (userDetails?.user?.id) {
        fetchUserDetails(userDetails.user.id);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to undo return');
    }
  };

  const handleAdvanceHoldStatus = async (hold) => {
    const advanceMap = { queued: 'pending', pending: 'reserved', reserved: 'borrowed', delivered: 'borrowed' };
    const next = advanceMap[hold.status];
    if (!next) return;
    const bookTitle = hold.book?.title || 'this book';
    if (!window.confirm(`Move "${bookTitle}" from ${hold.status} → ${next}?`)) return;
    try {
      const res = await axios.post(
        `${API}/admin/holds/${hold.id}/advance-status`,
        {},
        { headers: getAuthHeader() }
      );
      alert(res.data.message || 'Status updated');
      await fetchAllHolds();
      if (userDetails?.user?.id) fetchUserDetails(userDetails.user.id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update status');
    }
  };



  const handleWaiveFine = async (fineId, userName) => {
    if (!window.confirm(`Waive this fine for ${userName}?`)) return;
    
    try {
      await axios.put(`${API}/admin/fines/${fineId}/waive`, {}, { headers: getAuthHeader() });
      alert('Fine waived successfully!');
      fetchFines();
      fetchFinesSummary();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to waive fine');
    }
  };

  const handleAdjustFine = async (fineId) => {
    const newAmount = parseFloat(adjustAmount);
    if (isNaN(newAmount) || newAmount < 0) {
      alert('Please enter a valid amount');
      return;
    }
    
    try {
      await axios.put(`${API}/admin/fines/${fineId}/adjust`, { amount: newAmount }, { headers: getAuthHeader() });
      alert('Fine adjusted successfully!');
      setAdjustingFine(null);
      setAdjustAmount('');
      fetchFines();
      fetchFinesSummary();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to adjust fine');
    }
  };

  const handleClearUserFines = async (userId, userName) => {
    if (!window.confirm(`Clear ALL outstanding fines for ${userName}?`)) return;
    
    try {
      await axios.put(`${API}/admin/users/${userId}/clear-fines`, {}, { headers: getAuthHeader() });
      alert('All fines cleared successfully!');
      fetchFines();
      fetchFinesSummary();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to clear fines');
    }
  };

  const getFilteredFines = () => {
    switch (fineFilter) {
      case 'unpaid':
        return allFines.filter(f => !f.paid && !f.waived);
      case 'paid':
        return allFines.filter(f => f.paid);
      case 'waived':
        return allFines.filter(f => f.waived);
      default:
        return allFines;
    }
  };

  const handleBulkFineAction = async (action) => {
    const verb = action === 'paid' ? 'mark as PAID' : 'WAIVE';
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${selectedFineIds.length} selected fine(s)?`)) return;
    setBulkActing(true);
    try {
      const res = await axios.post(`${API}/admin/fines/bulk-action`, { fine_ids: selectedFineIds, action }, { headers: getAuthHeader() });
      alert(res.data.message + (res.data.skipped?.length ? `\n${res.data.skipped.length} skipped (already settled or not found)` : ''));
      setSelectedFineIds([]);
      fetchFines();
      fetchFinesSummary();
    } catch (error) {
      alert(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkActing(false);
    }
  };

  // Send renewal reminder emails
  const handleSendRenewalReminders = async () => {
    if (!window.confirm('Send renewal reminder emails to all members whose subscriptions expire within 7 days?')) return;
    
    setSendingRenewalReminders(true);
    try {
      const response = await axios.post(`${API}/admin/send-renewal-reminders`, {}, { headers: getAuthHeader() });
      alert(response.data.message);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send renewal reminders');
    } finally {
      setSendingRenewalReminders(false);
    }
  };

  // Send expired membership notices
  const handleSendExpiredNotices = async () => {
    if (!window.confirm('Send win-back emails to members whose subscriptions expired in the last 30 days?')) return;
    
    setSendingExpiredNotices(true);
    try {
      const response = await axios.post(`${API}/admin/send-expired-membership-notices`, {}, { headers: getAuthHeader() });
      alert(response.data.message);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send expired membership notices');
    } finally {
      setSendingExpiredNotices(false);
    }
  };

  // Adjust user subscription end date
  const handleAdjustSubscription = async (userId) => {
    if (!newSubscriptionEndDate) {
      alert('Please select a new end date');
      return;
    }
    
    setAdjustingSubscription(true);
    try {
      const response = await axios.put(
        `${API}/admin/users/${userId}/adjust-subscription`, 
        { new_end_date: newSubscriptionEndDate },
        { headers: getAuthHeader() }
      );
      alert(response.data.message);
      setNewSubscriptionEndDate('');
      // Refresh user details
      handleUserClick(userDetails.user);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to adjust subscription');
    } finally {
      setAdjustingSubscription(false);
    }
  };

  // Fix staff subscriptions
  const handleFixStaffSubscriptions = async () => {
    if (!window.confirm('Set all staff members to active subscription status?')) return;
    
    setFixingStaff(true);
    try {
      const response = await axios.post(`${API}/admin/fix-staff-subscriptions`, {}, { headers: getAuthHeader() });
      alert(response.data.message);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to fix staff subscriptions');
    } finally {
      setFixingStaff(false);
    }
  };

  const handleSaveMemberProfile = async (userId) => {
    setSavingMember(true);
    try {
      const updateData = {};
      if (editMemberData.name.trim()) updateData.name = editMemberData.name.trim();
      if (editMemberData.email.trim()) updateData.email = editMemberData.email.trim();
      if (editMemberData.phone.trim()) updateData.phone = editMemberData.phone.trim();

      await axios.put(`${API}/admin/users/${userId}/profile`, updateData, { headers: getAuthHeader() });
      setEditingMember(false);
      fetchUsers();
      fetchUserDetails(userId);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update member profile');
    } finally {
      setSavingMember(false);
    }
  };

  // Recalculate advance payments
  const handleRecalculateAdvancePayments = async () => {
    if (!window.confirm('Recalculate subscription end dates for members who paid in advance?\n\nThis will extend dates for members whose subscriptions were incorrectly calculated.')) return;
    
    setRecalculatingPayments(true);
    try {
      const response = await axios.post(`${API}/admin/recalculate-advance-payments`, {}, { headers: getAuthHeader() });
      
      if (response.data.fixes && response.data.fixes.length > 0) {
        const fixList = response.data.fixes.map(f => `${f.name}: +${f.days_added} days`).join('\n');
        alert(`${response.data.message}\n\nFixed:\n${fixList}`);
      } else {
        alert(response.data.message + '\n\nNo corrections needed.');
      }
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to recalculate payments');
    } finally {
      setRecalculatingPayments(false);
    }
  };

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API}/admin/dashboard-stats`, { headers: getAuthHeader() });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Borrow rules
  const fetchBorrowRules = async () => {
    setLoadingBorrowRules(true);
    try {
      const response = await axios.get(`${API}/admin/borrow-rules`, { headers: getAuthHeader() });
      setBorrowRules(response.data.rules || []);
    } catch (error) {
      console.error('Failed to fetch borrow rules:', error);
    } finally {
      setLoadingBorrowRules(false);
    }
  };

  const handleAddBorrowRule = async () => {
    if (!newRuleCategory.trim() || !newRuleDays) return;
    setAddingRule(true);
    try {
      await axios.post(`${API}/admin/borrow-rules`, {
        category: newRuleCategory.trim(),
        borrow_days: parseInt(newRuleDays)
      }, { headers: getAuthHeader() });
      setNewRuleCategory('');
      setNewRuleDays('');
      fetchBorrowRules();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add rule');
    } finally {
      setAddingRule(false);
    }
  };

  const handleUpdateBorrowRule = async (ruleId) => {
    if (!editRuleDays) return;
    try {
      await axios.put(`${API}/admin/borrow-rules/${ruleId}`, {
        borrow_days: parseInt(editRuleDays)
      }, { headers: getAuthHeader() });
      setEditingRule(null);
      setEditRuleDays('');
      fetchBorrowRules();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update rule');
    }
  };

  const handleDeleteBorrowRule = async (ruleId, category) => {
    if (!window.confirm(`Remove the custom borrow period for "${category}"? Books in this category will revert to the default 21-day period.`)) return;
    try {
      await axios.delete(`${API}/admin/borrow-rules/${ruleId}`, { headers: getAuthHeader() });
      fetchBorrowRules();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete rule');
    }
  };

  // Fetch add-ons
  const fetchAddons = async () => {
    setLoadingAddons(true);
    try {
      const response = await axios.get(`${API}/admin/addons`, { headers: getAuthHeader() });
      setAddons(response.data.addons || []);
    } catch (error) {
      console.error('Failed to fetch add-ons:', error);
    } finally {
      setLoadingAddons(false);
    }
  };

  const fetchAddonTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/addons/templates`, { headers: getAuthHeader() });
      setAddonTemplates(response.data.templates || {});
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const _resetNewAddon = () => setNewAddon({
    name: '',
    description: '',
    base_price: 0,
    addon_type: 'workshop',
    billing_frequency: 'one-time',
    image_url: '',
    event_datetime: '',
    event_end_datetime: '',
    location: '',
    payment_mode: 'one_time',
    max_capacity: null,
    allow_non_members: false,
    non_member_price: null,
    attendance_policy: 'reservation',
    allow_free_rsvp: false,
    external_registration_url: '',
    ticket_categories: [],
    features: [],
    visible_to_members: true
  });

  const openEditAddon = (addon) => {
    // Convert ISO datetime to the format datetime-local expects (YYYY-MM-DDTHH:MM)
    const toInput = (iso) => {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch { return ''; }
    };
    const dtForInput = toInput(addon.event_datetime);
    const dtEndForInput = toInput(addon.event_end_datetime);
    setNewAddon({
      name: addon.name || '',
      description: addon.description || '',
      base_price: Number(addon.base_price) || 0,
      addon_type: addon.addon_type || 'workshop',
      billing_frequency: addon.billing_frequency || 'one-time',
      image_url: addon.image_url || '',
      event_datetime: dtForInput,
      event_end_datetime: dtEndForInput,
      location: addon.location || '',
      payment_mode: addon.payment_mode || 'one_time',
      max_capacity: addon.max_capacity ?? null,
      allow_non_members: !!addon.allow_non_members,
      non_member_price: addon.non_member_price ?? null,
      attendance_policy: addon.attendance_policy === 'walk_ins' ? 'walk_ins' : 'reservation',
      allow_free_rsvp: !!addon.allow_free_rsvp,
      external_registration_url: addon.external_registration_url || '',
      ticket_categories: (addon.ticket_categories || []).map(c => ({ ...c, deadline: toInput(c.deadline) })),
      features: Array.isArray(addon.features) ? addon.features : [],
      visible_to_members: addon.visible_to_members !== false
    });
    setEditingAddonId(addon.id);
    setShowCreateAddon(true);
  };

  const handleSaveAddon = async () => {
    if (newAddon.event_datetime && newAddon.event_end_datetime && new Date(newAddon.event_end_datetime) <= new Date(newAddon.event_datetime)) {
      alert('Event end time must be after the start time');
      return;
    }
    try {
      // Normalize event datetimes to ISO if present
      const payload = { ...newAddon };
      if (payload.event_datetime) {
        try {
          payload.event_datetime = new Date(payload.event_datetime).toISOString();
        } catch { /* leave as-is; backend will reject if malformed */ }
      } else {
        payload.event_datetime = null;
      }
      if (payload.event_end_datetime) {
        try {
          payload.event_end_datetime = new Date(payload.event_end_datetime).toISOString();
        } catch { /* leave as-is */ }
      } else {
        payload.event_end_datetime = null;
      }
      payload.external_registration_url = (payload.external_registration_url || '').trim() || null;
      payload.ticket_categories = (payload.ticket_categories || [])
        .filter(c => (c.name || '').trim())
        .map(c => ({
          id: c.id,
          name: c.name.trim(),
          member_price: Math.max(0, parseFloat(c.member_price) || 0),
          non_member_price: (c.non_member_price === '' || c.non_member_price == null) ? null : Math.max(0, parseFloat(c.non_member_price) || 0),
          capacity: (c.capacity === '' || c.capacity == null) ? null : Math.max(1, parseInt(c.capacity, 10) || 1),
          deadline: c.deadline ? new Date(c.deadline).toISOString() : null,
          sold: c.sold || 0,
        }));
      if (editingAddonId) {
        await axios.put(`${API}/admin/addons/${editingAddonId}`, payload, { headers: getAuthHeader() });
        alert('Add-on updated successfully');
      } else {
        const response = await axios.post(`${API}/admin/addons`, payload, { headers: getAuthHeader() });
        alert(response.data.message);
      }
      setShowCreateAddon(false);
      setEditingAddonId(null);
      _resetNewAddon();
      fetchAddons();
    } catch (error) {
      alert(error.response?.data?.detail || (editingAddonId ? 'Failed to update add-on' : 'Failed to create add-on'));
    }
  };

  const handleCreateAddon = handleSaveAddon; // back-compat alias for existing button onClick

  const handleUploadAddonImage = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      alert('Only JPG, PNG, or WEBP images are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB.');
      return;
    }
    setUploadingAddonImage(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/admin/addons/upload-image`, form, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      let url = res.data.image_url || '';
      // Normalize: backend may return a relative '/api/...' path
      if (url.startsWith('/api/')) {
        url = `${API.replace(/\/api$/, '')}${url}`;
      }
      setNewAddon((prev) => ({ ...prev, image_url: url }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingAddonImage(false);
    }
  };

  const openRsvpModal = async (addon) => {
    setRsvpAddon(addon);
    setRsvpData(null);
    setLoadingRsvp(true);
    try {
      const res = await axios.get(`${API}/admin/addons/${addon.id}/subscribers`, { headers: getAuthHeader() });
      setRsvpData(res.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to load RSVPs');
      setRsvpAddon(null);
    } finally {
      setLoadingRsvp(false);
    }
  };

  const printCheckinSheet = () => {
    if (!rsvpAddon || !rsvpData?.subscribers?.length) return;
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const when = rsvpAddon.event_datetime
      ? new Date(rsvpAddon.event_datetime).toLocaleString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' })
      : '';
    const rows = [];
    rsvpData.subscribers.forEach((s) => {
      rows.push({ name: s.name, type: s.ticket_category || 'General', contact: [s.email, s.phone].filter(Boolean).join(' · '), paid: s.price_paid > 0 ? `₹${s.price_paid}` : 'Free' });
      (s.guests || []).forEach((g) => {
        rows.push({ name: `${g.name} — guest of ${s.name}`, type: s.ticket_category || 'General', contact: g.email || '', paid: 'incl.' });
      });
    });
    const walkins = rsvpAddon.attendance_policy === 'walk_ins';
    const rowHtml = (r) => `<tr><td class="box"><span></span></td><td>${esc(r.name)}</td><td>${esc(r.type)}</td><td>${esc(r.contact)}</td><td>${esc(r.paid)}</td></tr>`;
    const blank = '<tr><td class="box"><span></span></td><td class="line"></td><td>Walk-in</td><td class="line"></td><td class="line"></td></tr>';
    const html = `<!doctype html><html><head><title>Check-in — ${esc(rsvpAddon.name)}</title><style>
      @page { size: A4; margin: 18mm; }
      body { font-family: Georgia, 'Times New Roman', serif; color: #1c1a24; }
      h1 { font-size: 22px; margin: 0 0 2px; font-weight: normal; }
      .meta { font-size: 12px; letter-spacing: 1px; text-transform: uppercase; color: #555; margin-bottom: 4px; }
      .counts { font-size: 13px; margin: 8px 0 16px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      th { text-align: left; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; border-bottom: 2px solid #1c1a24; padding: 6px 8px; }
      td { border-bottom: 1px solid #ccc; padding: 9px 8px; vertical-align: middle; }
      td.box span { display: inline-block; width: 14px; height: 14px; border: 1.5px solid #1c1a24; }
      td.line { border-bottom: 1px solid #ccc; min-width: 120px; }
      .foot { margin-top: 24px; font-size: 11px; color: #777; font-style: italic; }
    </style></head><body>
      <h1>${esc(rsvpAddon.name)} — check-in sheet</h1>
      <div class="meta">${esc(when)} · The Hyderabad Library</div>
      <div class="counts">${rows.length} expected${walkins ? ' · walk-ins welcome (blank rows below)' : ''}</div>
      <table>
        <thead><tr><th>✓</th><th>Name</th><th>Ticket</th><th>Contact</th><th>Paid</th></tr></thead>
        <tbody>${rows.map(rowHtml).join('')}${walkins ? Array(6).fill(blank).join('') : ''}</tbody>
      </table>
      <div class="foot">Printed ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })} — thehyderabadlibrary.com</div>
    <script>window.onload = function(){ window.print(); };</script></body></html>`;
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow pop-ups to print the check-in sheet.'); return; }
    w.document.write(html);
    w.document.close();
  };

  const [sendingFollowup, setSendingFollowup] = useState(false);

  const handleSendFollowup = async () => {
    if (!rsvpAddon) return;
    const already = rsvpAddon.followup_sent_at
      ? `\n\n(Note: a thank-you was already sent on ${new Date(rsvpAddon.followup_sent_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}.)`
      : '';
    if (!window.confirm(`Send a "Thanks for coming — here's what's on next" email to all attendees (guests included) of "${rsvpAddon.name}"?${already}`)) return;
    setSendingFollowup(true);
    try {
      const response = await axios.post(`${API}/admin/addons/${rsvpAddon.id}/send-followup`, {}, { headers: getAuthHeader() });
      alert(response.data.message);
      fetchAddons();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send follow-up');
    } finally {
      setSendingFollowup(false);
    }
  };

  const handleSendAddonReminder = async () => {
    if (!rsvpAddon) return;
    if (!window.confirm(`Send a reminder email to all ${rsvpData?.subscribers?.length || 0} subscriber(s) of "${rsvpAddon.name}"?`)) return;
    setSendingReminder(true);
    try {
      const res = await axios.post(`${API}/admin/addons/${rsvpAddon.id}/send-reminder`, {}, { headers: getAuthHeader() });
      alert(res.data.message || 'Reminders queued');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const _resetCashSubForm = () => {
    setShowAddCashSubscriber(false);
    setCashSubMode('existing');
    setCashSubUserQuery('');
    setCashSubUserResults([]);
    setCashSubSelectedUser(null);
    setCashSubWalkin({ name: '', email: '', phone: '' });
    setCashSubAmount(0);
    setCashSubPaymentMethod('cash');
    setCashSubNotes('');
    setCashSubGuests([]);
    setCashSubCategoryId('');
  };

  const searchUsers = async (q) => {
    setCashSubUserQuery(q);
    setCashSubSelectedUser(null);
    if (!q || q.trim().length < 2) {
      setCashSubUserResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(q.trim())}`, { headers: getAuthHeader() });
      setCashSubUserResults(res.data || []);
    } catch (err) {
      setCashSubUserResults([]);
    }
  };

  const handleAddCashSubscriber = async () => {
    if (!rsvpAddon) return;
    const payload = {
      amount_paid: Number(cashSubAmount) || 0,
      payment_method: cashSubPaymentMethod,
      notes: cashSubNotes.trim() || null,
      quantity: 1 + cashSubGuests.length,
      guests: cashSubGuests,
      ticket_category_id: cashSubCategoryId || null
    };
    // Validate guest entries
    for (let i = 0; i < cashSubGuests.length; i++) {
      if (!cashSubGuests[i].name.trim() || !cashSubGuests[i].email.trim()) {
        alert(`Guest #${i + 1}: name and email are required.`);
        return;
      }
    }
    if (cashSubMode === 'existing') {
      if (!cashSubSelectedUser) { alert('Please pick a user from the search results.'); return; }
      payload.user_id = cashSubSelectedUser.id;
    } else {
      if (!cashSubWalkin.name.trim() || !cashSubWalkin.email.trim()) {
        alert('Walk-in name and email are required.');
        return;
      }
      payload.name = cashSubWalkin.name.trim();
      payload.email = cashSubWalkin.email.trim();
      payload.phone = cashSubWalkin.phone.trim() || null;
    }
    setSavingCashSub(true);
    try {
      const res = await axios.post(`${API}/admin/addons/${rsvpAddon.id}/subscribers/manual`, payload, { headers: getAuthHeader() });
      alert(res.data.message || 'Added.');
      _resetCashSubForm();
      // refresh the RSVP list
      await openRsvpModal(rsvpAddon);
      fetchAddons();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add subscriber');
    } finally {
      setSavingCashSub(false);
    }
  };

  const handleRemoveSubscriber = async (sub) => {
    if (!rsvpAddon) return;
    if (!window.confirm(`Remove ${sub.name || sub.email} from "${rsvpAddon.name}"?`)) return;
    setRemovingSubId(sub.subscription_id);
    try {
      await axios.delete(`${API}/admin/addons/${rsvpAddon.id}/subscribers/${sub.subscription_id}`, { headers: getAuthHeader() });
      await openRsvpModal(rsvpAddon);
      fetchAddons();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to remove subscriber');
    } finally {
      setRemovingSubId(null);
    }
  };

  const handleUpdateAddon = async (addonId, updates) => {
    try {
      await axios.put(`${API}/admin/addons/${addonId}`, updates, { headers: getAuthHeader() });
      fetchAddons();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update add-on');
    }
  };

  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) return;
    try {
      const response = await axios.delete(`${API}/admin/addons/${addonId}`, { headers: getAuthHeader() });
      alert(response.data.message);
      fetchAddons();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete add-on');
    }
  };

  const applyTemplate = (templateKey) => {
    const template = addonTemplates[templateKey];
    if (template) {
      setNewAddon({
        ...newAddon,
        ...template,
        visible_to_members: false
      });
    }
  };

  // Update user subscription
  const handleUpdateSubscription = async () => {
    if (!selectedUser) return;
    
    setUpdatingSubscription(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/subscription`, {
        subscription_status: subscriptionUpdate.status,
        subscription_plan: subscriptionUpdate.plan,
        duration_months: parseInt(subscriptionUpdate.duration) || 1,
        payment_note: subscriptionUpdate.note || null
      }, { headers: getAuthHeader() });
      
      alert(`✅ Subscription updated successfully!${subscriptionUpdate.note ? '\nPayment note added.' : ''}`);
      setShowSubscriptionModal(false);
      setSubscriptionUpdate({ status: 'active', plan: 'cash', duration: 1, note: '' });
      
      // Refresh user details
      fetchUserDetails(selectedUser.id);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setUpdatingSubscription(false);
    }
  };

  // Fetch recommendation count on initial load
  useEffect(() => {
    const fetchRecommendationCount = async () => {
      try {
        const response = await axios.get(`${API}/admin/recommendation-requests`, { headers: getAuthHeader() });
        setRecommendationRequests(response.data);
      } catch (error) {
        console.error('Failed to fetch recommendation requests:', error);
      }
    };
    fetchRecommendationCount();
  }, []);

  const [extensionRequests, setExtensionRequests] = useState([]);
  const [loadingExtensions, setLoadingExtensions] = useState(false);

  const fetchPendingReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reviews/pending`, { headers: getAuthHeader() });
      setPendingReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch pending reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupInfo = async () => {
    setLoadingBackup(true);
    try {
      const response = await axios.get(`${API}/admin/export/all`, { headers: getAuthHeader() });
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch backup info:', error);
    } finally {
      setLoadingBackup(false);
    }
  };

  const handleDownloadBackup = async (type) => {
    setDownloadingBackup(type);
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_backup_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to download ${type} backup`);
    } finally {
      setDownloadingBackup(null);
    }
  };

  const fetchComingSoonCalendar = async () => {
    setLoadingCalendar(true);
    try {
      const response = await axios.get(`${API}/books/coming-soon-calendar`, { headers: getAuthHeader() });
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch coming soon calendar:', error);
    } finally {
      setLoadingCalendar(false);
    }
  };

  const fetchRecommendationRequests = async () => {
    setLoadingRecommendations(true);
    try {
      const response = await axios.get(`${API}/admin/recommendation-requests`, { headers: getAuthHeader() });
      setRecommendationRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendation requests:', error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const searchBooksForRecommendation = async (query) => {
    if (!query.trim()) {
      setRecommendationBookResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/books?search=${encodeURIComponent(query)}&limit=10`, { headers: getAuthHeader() });
      setRecommendationBookResults(response.data.books || []);
    } catch (error) {
      console.error('Failed to search books:', error);
    }
  };

  const handleSendRecommendations = async (requestId) => {
    if (selectedRecommendations.length === 0) {
      alert('Please select at least one book to recommend');
      return;
    }
    
    try {
      await axios.post(
        `${API}/admin/recommendation-requests/${requestId}/respond`,
        selectedRecommendations.map(b => b.id),
        { headers: getAuthHeader() }
      );
      alert(`✅ Sent ${selectedRecommendations.length} book recommendations!`);
      setSelectedRequest(null);
      setSelectedRecommendations([]);
      setRecommendationBookSearch('');
      fetchRecommendationRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send recommendations');
    }
  };

  const handleDismissRequest = async (requestId) => {
    if (!window.confirm('Dismiss this recommendation request?')) return;
    
    try {
      await axios.delete(`${API}/admin/recommendation-requests/${requestId}`, { headers: getAuthHeader() });
      fetchRecommendationRequests();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to dismiss request');
    }
  };

  const [fixingInventory, setFixingInventory] = useState(false);
  
  const handleFixAllInventory = async () => {
    if (!window.confirm('This will recalculate available_copies for all books based on active holds. Continue?')) return;
    
    setFixingInventory(true);
    try {
      const response = await axios.post(`${API}/admin/fix-all-inventory`, {}, { headers: getAuthHeader() });
      const data = response.data;
      if (data.fixed_count > 0) {
        alert(`Fixed inventory for ${data.fixed_count} book(s):\n\n${data.details.map(d => `• ${d.book_title}: ${d.previous} → ${d.corrected}`).join('\n')}`);
      } else {
        alert('All book inventories are already correct!');
      }
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to fix inventory');
    } finally {
      setFixingInventory(false);
    }
  };

  const searchBooksForMemberAction = async (query) => {
    if (!query.trim()) {
      setMemberActionBookResults([]);
      return;
    }
    
    setSearchingMemberBooks(true);
    try {
      const response = await axios.get(`${API}/books?search=${encodeURIComponent(query)}&limit=10`, { headers: getAuthHeader() });
      setMemberActionBookResults(response.data.books || []);
    } catch (error) {
      console.error('Failed to search books:', error);
    } finally {
      setSearchingMemberBooks(false);
    }
  };

  // Search members by name, email, or membership number (for Checkout tab)
  const searchMembers = async (query) => {
    if (!query || query.length < 2) {
      setMemberSearchResults([]);
      return;
    }
    
    setSearchingMembers(true);
    try {
      const response = await axios.get(`${API}/admin/search-members?q=${encodeURIComponent(query)}`, { headers: getAuthHeader() });
      setMemberSearchResults(response.data || []);
    } catch (error) {
      console.error('Failed to search members:', error);
    } finally {
      setSearchingMembers(false);
    }
  };

  // Search members for Add History tab
  const searchHistoryMembers = async (query) => {
    if (!query || query.length < 2) {
      setHistoryMemberSearchResults([]);
      return;
    }
    
    setSearchingHistoryMembers(true);
    try {
      const response = await axios.get(`${API}/admin/search-members?q=${encodeURIComponent(query)}`, { headers: getAuthHeader() });
      setHistoryMemberSearchResults(response.data || []);
    } catch (error) {
      console.error('Failed to search members:', error);
    } finally {
      setSearchingHistoryMembers(false);
    }
  };

  // Extend loan for a borrowed book
  const handleExtendLoan = async (holdId, days) => {
    try {
      const response = await axios.post(`${API}/staff/extend-loan`, {
        hold_id: holdId,
        days: days
      }, { headers: getAuthHeader() });
      
      alert(`Success! ${response.data.message}\nNew due date: ${new Date(response.data.new_due_date).toLocaleDateString()}`);
      setExtendingLoan(null);
      setExtensionDays(7);
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to extend loan');
    }
  };

  const handlePlaceHoldForMember = async () => {
    const memberEmail = selectedMember?.email || memberActionEmail;
    if (!memberEmail || selectedBooksForAction.length === 0) {
      alert('Please select a member and at least one book');
      return;
    }
    
    setProcessingMemberAction(true);
    const ok = [];
    const failed = [];
    for (const book of selectedBooksForAction) {
      try {
        const payload = { member_email: memberEmail, book_id: book.id, for_child: checkoutForChild };
        if (memberHoldRequestedDate) payload.requested_date = memberHoldRequestedDate;
        const response = await axios.post(`${API}/staff/hold-for-member`, payload, { headers: getAuthHeader() });
        ok.push(`${book.title} — ${response.data.status || 'hold placed'}`);
      } catch (error) {
        failed.push(`${book.title}: ${error.response?.data?.detail || 'failed'}`);
      }
    }
    let msg = ok.length ? `✅ Holds placed (${ok.length}):\n${ok.join('\n')}` : '';
    if (memberHoldRequestedDate && ok.length) msg += `\nRequested pickup: ${new Date(memberHoldRequestedDate).toLocaleDateString()}`;
    if (failed.length) msg += `${msg ? '\n\n' : ''}⚠️ Failed (${failed.length}):\n${failed.join('\n')}`;
    alert(msg || 'Nothing processed');
    if (ok.length) {
      setMemberActionEmail('');
      setSelectedMember(null);
      setMemberSearchQuery('');
      setMemberSearchResults([]);
      setSelectedBooksForAction(failed.length ? selectedBooksForAction.filter(b => failed.some(f => f.startsWith(b.title + ':'))) : []);
      setMemberActionBookSearch('');
      setMemberActionBookResults([]);
      setMemberHoldRequestedDate('');
    }
    setProcessingMemberAction(false);
  };

  const handleCheckoutForMember = async () => {
    const memberEmail = selectedMember?.email || memberActionEmail;
    if (!memberEmail || selectedBooksForAction.length === 0) {
      alert('Please select a member and at least one book');
      return;
    }
    
    setProcessingMemberAction(true);
    const ok = [];
    const failed = [];
    for (const book of selectedBooksForAction) {
      try {
        const response = await axios.post(`${API}/staff/checkout-for-member`, {
          member_email: memberEmail,
          book_id: book.id,
          due_days: 21,
          for_child: checkoutForChild
        }, { headers: getAuthHeader() });
        ok.push(`${book.title} — due ${new Date(response.data.due_date).toLocaleDateString()}`);
      } catch (error) {
        failed.push(`${book.title}: ${error.response?.data?.detail || 'failed'}`);
      }
    }
    let msg = ok.length ? `✅ Checked out (${ok.length}):\n${ok.join('\n')}` : '';
    if (failed.length) msg += `${msg ? '\n\n' : ''}⚠️ Failed (${failed.length}):\n${failed.join('\n')}`;
    alert(msg || 'Nothing processed');
    if (ok.length) {
      setMemberActionEmail('');
      setSelectedMember(null);
      setMemberSearchQuery('');
      setMemberSearchResults([]);
      setSelectedBooksForAction(failed.length ? selectedBooksForAction.filter(b => failed.some(f => f.startsWith(b.title + ':'))) : []);
      setMemberActionBookSearch('');
      setMemberActionBookResults([]);
    }
    setProcessingMemberAction(false);
  };


  const handleApproveReview = async (reviewId) => {
    setProcessingReview(reviewId);
    try {
      await axios.post(`${API}/reviews/${reviewId}/approve`, {}, { headers: getAuthHeader() });
      fetchPendingReviews();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to approve review');
    } finally {
      setProcessingReview(null);
    }
  };

  const handleRejectReview = async (reviewId) => {
    setProcessingReview(reviewId);
    try {
      await axios.post(`${API}/reviews/${reviewId}/reject`, {}, { headers: getAuthHeader() });
      fetchPendingReviews();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reject review');
    } finally {
      setProcessingReview(null);
    }
  };

  const fetchExtensionRequests = async () => {
    setLoadingExtensions(true);
    try {
      const response = await axios.get(`${API}/holds/extension-requests`, { headers: getAuthHeader() });
      setExtensionRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch extension requests:', error);
    } finally {
      setLoadingExtensions(false);
    }
  };

  // Fetch extension requests when holds tab is active
  useEffect(() => {
    if (activeTab === 'holds') {
      fetchExtensionRequests();
    }
  }, [activeTab]);

  const handleApproveExtension = async (holdId, approved, newDueDate = null) => {
    try {
      await axios.put(`${API}/holds/${holdId}/approve-extension`, {
        approved,
        new_due_date: newDueDate
      }, { headers: getAuthHeader() });
      alert(approved ? 'Extension approved!' : 'Extension denied.');
      fetchExtensionRequests();
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to process extension');
    }
  };

  // Helper to check if a hold is overdue
  const isOverdue = (hold) => {
    // Check if borrowed or delivered hold is overdue
    if (!['borrowed', 'delivered'].includes(hold.status) || !hold.due_date) return false;
    return new Date(hold.due_date) < new Date();
  };

  // Filter holds based on selected status
  const getFilteredHolds = () => {
    let filtered = allHolds;
    
    switch (holdStatusFilter) {
      case 'pending':
        filtered = allHolds.filter(h => h.status === 'pending');
        break;
      case 'queued':
        filtered = allHolds.filter(h => h.status === 'queued');
        break;
      case 'reserved':
        filtered = allHolds.filter(h => h.status === 'reserved');
        break;
      case 'active':
        // Combined: borrowed + delivered (both are "active borrows")
        filtered = allHolds.filter(h => (h.status === 'borrowed' || h.status === 'delivered') && !isOverdue(h));
        break;
      case 'overdue':
        filtered = allHolds.filter(h => isOverdue(h));
        break;
      case 'extensions':
        filtered = allHolds.filter(h => h.extension_requested && h.extension_approved === null);
        break;
      case 'all':
      default:
        filtered = allHolds.filter(h => ['pending', 'queued', 'reserved', 'borrowed', 'delivered'].includes(h.status));
        break;
    }
    
    // Sort by status priority and date
    return filtered.sort((a, b) => {
      // Overdue items first
      const aOverdue = isOverdue(a);
      const bOverdue = isOverdue(b);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      
      // Then by status priority
      const statusOrder = { pending: 0, queued: 1, reserved: 2, borrowed: 3, delivered: 4 };
      const statusDiff = (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
      if (statusDiff !== 0) return statusDiff;
      
      // Then by requested_pickup_date (earliest first)
      const dateA = a.requested_pickup_date ? new Date(a.requested_pickup_date) : null;
      const dateB = b.requested_pickup_date ? new Date(b.requested_pickup_date) : null;
      
      if (dateA && dateB) {
        return dateA - dateB; // Earliest requested pickup first
      }
      if (dateA && !dateB) {
        return -1; // Holds with pickup date come first
      }
      if (!dateA && dateB) {
        return 1;
      }
      
      // If neither has pickup date, sort by hold_date (most recent first)
      return new Date(b.hold_date) - new Date(a.hold_date);
    });
  };

  // Count holds by status for filter badges
  const getStatusCounts = () => {
    const counts = {
      all: allHolds.filter(h => ['pending', 'queued', 'reserved', 'borrowed', 'delivered'].includes(h.status)).length,
      pending: allHolds.filter(h => h.status === 'pending').length,
      queued: allHolds.filter(h => h.status === 'queued').length,
      reserved: allHolds.filter(h => h.status === 'reserved').length,
      active: allHolds.filter(h => (h.status === 'borrowed' || h.status === 'delivered') && !isOverdue(h)).length,
      overdue: allHolds.filter(h => isOverdue(h)).length,
      extensions: allHolds.filter(h => h.extension_requested && h.extension_approved === null).length
    };
    return counts;
  };

  const fetchProcurements = async () => {
    setLoadingProcurements(true);
    try {
      const response = await axios.get(`${API}/books/procurement/report`, { headers: getAuthHeader() });
      setProcurements(response.data);
    } catch (error) {
      console.error('Failed to fetch procurements:', error);
    } finally {
      setLoadingProcurements(false);
    }
  };

  // Fetch authors and categories for dropdowns
  const fetchAuthorsAndCategories = async () => {
    try {
      const [authorsRes, categoriesRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/books/authors`),
        axios.get(`${API}/books/categories`),
        axios.get(`${API}/books/procurement-sources`)
      ]);
      setAllAuthors(authorsRes.data || []);
      setAllCategories(categoriesRes.data || []);
      setAllVendors(vendorsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch authors/categories/vendors:', error);
    }
  };

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (authorDropdownRef.current && !authorDropdownRef.current.contains(e.target)) {
        setShowAuthorDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(e.target)) {
        setShowVendorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAllHolds = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/holds/all`, { headers: getAuthHeader() });
      // Backend now enriches `book` and `user` server-side (1 query each via $in bulk),
      // so no client-side N+1 fan-out.
      const holdsWithDetails = response.data;

      // Sort by requested_pickup_date (earliest first), then by hold_date
      const sortedHolds = [...holdsWithDetails].sort((a, b) => {
        const dateA = a.requested_pickup_date ? new Date(a.requested_pickup_date) : null;
        const dateB = b.requested_pickup_date ? new Date(b.requested_pickup_date) : null;
        if (dateA && dateB) return dateA - dateB;
        if (dateA && !dateB) return -1;
        if (!dateA && dateB) return 1;
        return new Date(b.hold_date) - new Date(a.hold_date);
      });

      setAllHolds(sortedHolds);
    } catch (error) {
      console.error('Failed to fetch holds:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBorrows = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/borrows/all`, { headers: getAuthHeader() });
      // Backend enriches `book` and `user` server-side.
      setAllBorrows(response.data || []);
    } catch (error) {
      console.error('Failed to fetch borrows:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStaff = async () => {
    const makingStaff = userDetails.user.role !== 'staff';
    const msg = makingStaff
      ? `Make ${userDetails.user.name} STAFF?\n\nThey will get full access to the admin dashboard, checkouts, fines and member data.`
      : `Remove staff access from ${userDetails.user.name}?\n\nThey will become a regular member.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.put(`${API}/admin/users/${userDetails.user.id}/role`, { role: makingStaff ? 'staff' : 'member' }, { headers: getAuthHeader() });
      alert(res.data.message);
      fetchUserDetails(userDetails.user.id);
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeader() });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    setLoadingUserDetails(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/details`, { headers: getAuthHeader() });
      setUserDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      alert('Failed to load user details');
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserDetails(user.id);
  };

  const closeUserModal = () => {
    setSelectedUser(null);
    setUserDetails(null);
  };

  // Book library card functions
  const handleBookClick = async (book) => {
    if (!book || !book.id) return;
    setLoadingBookCard(true);
    try {
      const response = await axios.get(`${API}/books/${book.id}/library-card`, { headers: getAuthHeader() });
      setBookLibraryCard(response.data);
    } catch (error) {
      console.error('Failed to fetch book library card:', error);
      alert('Failed to load book details');
    } finally {
      setLoadingBookCard(false);
    }
  };

  const closeBookModal = () => {
    setBookLibraryCard(null);
  };

  const handleScanISBN = async (e) => {
    e.preventDefault();
    if (!isbn.trim()) return;

    setScanning(true);
    setScanResult(null);

    try {
      const response = await axios.post(
        `${API}/books/scan-isbn`,
        { isbn: isbn.trim() },
        { headers: getAuthHeader() }
      );
      setScanResult({ success: true, book: response.data });
      setIsbn('');
    } catch (error) {
      setScanResult({
        success: false,
        error: error.response?.data?.detail || 'Failed to scan ISBN'
      });
    } finally {
      setScanning(false);
    }
  };

  const handleReturnBook = async (borrowId, bookTitle) => {
    if (!window.confirm(`Return "${bookTitle}"?`)) return;

    try {
      await axios.put(`${API}/borrows/${borrowId}/return`, {}, { headers: getAuthHeader() });
      alert('Book returned successfully!');
      fetchAllBorrows();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to return book');
    }
  };

  // Manual book entry handler
  const handleAddBook = async (e) => {
    e.preventDefault();
    setAddingBook(true);
    setBookResult(null);
    
    try {
      const bookData = {
        title: manualBook.title,
        authors: selectedAuthors.length > 0 ? selectedAuthors : manualBook.authors.split(',').map(a => a.trim()).filter(a => a),
        isbn: manualBook.isbn || `MANUAL-${Date.now()}`,
        publisher: manualBook.publisher || null,
        published_date: manualBook.published_date || null,
        description: manualBook.description || null,
        categories: selectedCategories.length > 0 ? selectedCategories : manualBook.categories.split(',').map(c => c.trim()).filter(c => c),
        cover_image_url: manualBook.cover_image_url || null,
        total_copies: parseInt(manualBook.total_copies) || 1,
        available_copies: parseInt(manualBook.total_copies) || 1,
        available_from: manualBook.available_from || null,
        // Procurement details
        procurement_type: manualBook.procurement_type || 'purchased',
        procurement_price: manualBook.purchase_price ? parseFloat(manualBook.purchase_price) : null,
        procurement_source: vendorInput.trim() || manualBook.vendor || null,
        procurement_date: manualBook.procurement_date || null,
        procurement_notes: manualBook.procurement_notes || null
      };
      
      await axios.post(`${API}/books/manual`, bookData, { headers: getAuthHeader() });
      setBookResult({ success: true, message: `"${manualBook.title}" added successfully!` });
      setManualBook({
        title: '', authors: '', isbn: '', publisher: '', published_date: '',
        description: '', categories: '', cover_image_url: '', total_copies: 1, available_from: '',
        procurement_type: 'purchased', purchase_price: '', vendor: '', procurement_date: '', procurement_notes: ''
      });
      setSelectedAuthors([]);
      setSelectedCategories([]);
      setAuthorInput('');
      setCategoryInput('');
      setVendorInput('');
    } catch (error) {
      setBookResult({
        success: false,
        error: error.response?.data?.detail || 'Failed to add book'
      });
    } finally {
      setAddingBook(false);
    }
  };

  // Search books for history entry
  const searchBooksForHistory = async (query) => {
    if (!query || query.length < 2) {
      setBookSearchResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/books?search=${encodeURIComponent(query)}&limit=5`, { headers: getAuthHeader() });
      setBookSearchResults(response.data.books || []);
    } catch (error) {
      console.error('Failed to search books:', error);
    }
  };

  // Manual history entry handler
  const handleAddHistory = async (e) => {
    e.preventDefault();
    if (!selectedBook) {
      alert('Please select a book from the search results');
      return;
    }
    
    setAddingHistory(true);
    setHistoryResult(null);
    
    try {
      const historyData = {
        user_email: historyEntry.user_email,
        book_id: selectedBook.id,
        borrow_date: historyEntry.borrow_date,
        return_date: historyEntry.return_date || null
      };
      
      await axios.post(`${API}/borrows/manual-history`, historyData, { headers: getAuthHeader() });
      setHistoryResult({ success: true, message: 'Borrow history added successfully!' });
      setHistoryEntry({ user_email: '', book_title: '', borrow_date: '', return_date: '' });
      setSelectedBook(null);
      setBookSearchResults([]);
    } catch (error) {
      setHistoryResult({
        success: false,
        error: error.response?.data?.detail || 'Failed to add history'
      });
    } finally {
      setAddingHistory(false);
    }
  };

  // Hold workflow actions
  const handleReserveHold = async (holdId, bookTitle, force = false) => {
    const message = force 
      ? `FORCE RESERVE "${bookTitle}"? This will reserve even though no copies are available (creates negative inventory).`
      : `Reserve "${bookTitle}" for this patron? This will start the loan period countdown.`;
    
    if (!window.confirm(message)) return;
    
    try {
      const response = await axios.put(`${API}/holds/${holdId}/reserve?force=${force}`, {}, { headers: getAuthHeader() });
      const data = response.data;
      
      // Build success message with loan info
      let successMsg = 'Book reserved successfully!' + (force ? ' (forced)' : '');
      
      // Show warning if loan was shortened
      if (data.loan_shortened) {
        successMsg = `⚠️ LOAN PERIOD SHORTENED\n\n${data.message}\n\nDue Date: ${new Date(data.due_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}\nLoan Period: ${data.loan_days} days (instead of ${data.original_loan_days})`;
      } else {
        successMsg += `\n\nDue Date: ${new Date(data.due_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`;
      }
      
      alert(successMsg);
      fetchAllHolds();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reserve hold';
      // If no copies available, offer force option
      if (errorMsg.includes('No copies available') && !force) {
        if (window.confirm(`${errorMsg}\n\nWould you like to FORCE reserve anyway?`)) {
          handleReserveHold(holdId, bookTitle, true);
        }
      } else {
        alert(errorMsg);
      }
    }
  };

  const handleMarkBorrowed = async (holdId, bookTitle) => {
    if (!window.confirm(`Mark "${bookTitle}" as borrowed (picked up)?`)) return;
    
    try {
      await axios.put(`${API}/holds/${holdId}/borrow`, {}, { headers: getAuthHeader() });
      alert('Book marked as borrowed!');
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark as borrowed');
    }
  };

  const handleMarkDelivered = (hold) => {
    // Open batch delivery modal (lets staff pick sibling holds + record fee)
    setDeliveryModalHold(hold);
  };

  const handleReturnHold = async (holdId, bookTitle) => {
    if (!window.confirm(`Return "${bookTitle}"? This will complete the borrowing cycle.`)) return;
    
    try {
      await axios.put(`${API}/holds/${holdId}/return`, {}, { headers: getAuthHeader() });
      alert('Book returned successfully!');
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to return book');
    }
  };

  const handleCancelHold = async (holdId, bookTitle) => {
    if (!window.confirm(`Cancel hold for "${bookTitle}"?`)) return;
    
    try {
      await axios.delete(`${API}/holds/${holdId}`, { headers: getAuthHeader() });
      alert('Hold cancelled successfully!');
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel hold');
    }
  };

  const handlePromoteQueued = async (holdId, bookTitle, userName) => {
    if (!window.confirm(`Promote queued hold for "${bookTitle}" to pending status?\n\nThis will allow the member to borrow this book even if they have 5 active borrows.`)) return;
    
    try {
      await axios.put(`${API}/admin/holds/${holdId}/promote`, {}, { headers: getAuthHeader() });
      alert('Hold promoted to pending status!');
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to promote hold');
    }
  };

  const handleEditHold = (hold) => {
    setEditingHold(hold);
    setEditHoldData({
      preferred_date: hold.requested_pickup_date ? hold.requested_pickup_date.split('T')[0] : '',
      delivery_option: hold.delivery_option || 'self_pickup'
    });
  };

  const handleSaveHoldEdit = async () => {
    if (!editingHold) return;
    
    setSavingHold(true);
    try {
      await axios.put(`${API}/admin/holds/${editingHold.id}/edit`, editHoldData, { headers: getAuthHeader() });
      alert('Hold updated successfully!');
      setEditingHold(null);
      fetchAllHolds();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update hold');
    } finally {
      setSavingHold(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      queued: 'bg-orange-100 text-orange-700',
      reserved: 'bg-blue-100 text-blue-700',
      borrowed: 'bg-green-100 text-green-700',
      delivered: 'bg-purple-100 text-purple-700',
      returned: 'bg-gray-100 text-gray-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getHoldActions = (hold) => {
    const isDelivery = hold.delivery_option === 'paid_delivery';
    
    switch (hold.status) {
      case 'pending':
        return (
          <div className="flex gap-2 flex-wrap">
            {isDelivery ? (
              <button
                onClick={() => handleMarkDelivered(hold)}
                className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                data-testid={`deliver-hold-${hold.id}`}
              >
                Mark Delivered
              </button>
            ) : (
              <button
                onClick={() => handleReserveHold(hold.id, hold.book?.title)}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                data-testid={`reserve-hold-${hold.id}`}
              >
                Reserve
              </button>
            )}
            <button
              onClick={() => handleEditHold(hold)}
              className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
              data-testid={`edit-hold-${hold.id}`}
            >
              Edit
            </button>
            <button
              onClick={() => handleCancelHold(hold.id, hold.book?.title)}
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm"
              data-testid={`cancel-hold-${hold.id}`}
            >
              Cancel
            </button>
          </div>
        );
      case 'queued':
        return (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => handlePromoteQueued(hold.id, hold.book?.title, hold.user?.name)}
              className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors text-sm"
              data-testid={`promote-hold-${hold.id}`}
            >
              Promote to Pending
            </button>
            <button
              onClick={() => handleCancelHold(hold.id, hold.book?.title)}
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm"
              data-testid={`cancel-queued-${hold.id}`}
            >
              Cancel
            </button>
          </div>
        );
      case 'reserved':
        return (
          <div className="flex gap-2 flex-wrap">
            {isDelivery ? (
              <button
                onClick={() => handleMarkDelivered(hold)}
                className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                data-testid={`deliver-hold-${hold.id}`}
              >
                Mark Delivered
              </button>
            ) : (
              <button
                onClick={() => handleMarkBorrowed(hold.id, hold.book?.title)}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
                data-testid={`borrow-hold-${hold.id}`}
              >
                Mark Borrowed
              </button>
            )}
            <button
              onClick={() => handleEditHold(hold)}
              className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
              data-testid={`edit-hold-${hold.id}`}
            >
              Edit
            </button>
            <button
              onClick={() => handleCancelHold(hold.id, hold.book?.title)}
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        );
      case 'delivered':
        return (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => handleReturnHold(hold.id, hold.book?.title)}
              className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm"
              data-testid={`return-hold-${hold.id}`}
            >
              Mark Returned
            </button>
            {/* Extend Loan Button for delivered books */}
            {extendingLoan === hold.id ? (
              <div className="flex items-center gap-1">
                <select
                  value={extensionDays}
                  onChange={(e) => setExtensionDays(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                >
                  <option value={7}>+7 days</option>
                  <option value={14}>+14 days</option>
                  <option value={21}>+21 days</option>
                </select>
                <button
                  onClick={() => handleExtendLoan(hold.id, extensionDays)}
                  className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                >
                  ✓
                </button>
                <button
                  onClick={() => { setExtendingLoan(null); setExtensionDays(7); }}
                  className="px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white rounded text-xs"
                >
                  ✗
                </button>
              </div>
            ) : (
              <button
                onClick={() => setExtendingLoan(hold.id)}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                data-testid={`extend-loan-delivered-${hold.id}`}
              >
                Extend Loan
              </button>
            )}
            {/* Extension request actions for delivered books */}
            {hold.extension_requested && hold.extension_approved === null && (
              <div className="flex gap-1">
                <button
                  onClick={() => handleApproveExtension(hold.id, true)}
                  className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs"
                  title="Approve extension"
                >
                  ✓ Ext Request
                </button>
                <button
                  onClick={() => handleApproveExtension(hold.id, false)}
                  className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                  title="Deny extension"
                >
                  ✗ Ext Request
                </button>
              </div>
            )}
            {hold.due_date && (
              <p className="text-xs text-gray-500">
                Due: {new Date(hold.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
        );
      case 'borrowed':
        return (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => handleReturnHold(hold.id, hold.book?.title)}
              className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors text-sm"
              data-testid={`return-hold-${hold.id}`}
            >
              Mark Returned
            </button>
            {/* Extend Loan Button */}
            {extendingLoan === hold.id ? (
              <div className="flex items-center gap-1">
                <select
                  value={extensionDays}
                  onChange={(e) => setExtensionDays(parseInt(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                >
                  <option value={7}>+7 days</option>
                  <option value={14}>+14 days</option>
                  <option value={21}>+21 days</option>
                </select>
                <button
                  onClick={() => handleExtendLoan(hold.id, extensionDays)}
                  className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                >
                  ✓
                </button>
                <button
                  onClick={() => { setExtendingLoan(null); setExtensionDays(7); }}
                  className="px-2 py-1 bg-gray-400 hover:bg-gray-500 text-white rounded text-xs"
                >
                  ✗
                </button>
              </div>
            ) : (
              <button
                onClick={() => setExtendingLoan(hold.id)}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                data-testid={`extend-loan-${hold.id}`}
              >
                Extend Loan
              </button>
            )}
            {/* Extension request actions */}
            {hold.extension_requested && hold.extension_approved === null && (
              <div className="flex gap-1">
                <button
                  onClick={() => handleApproveExtension(hold.id, true)}
                  className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs"
                  title="Approve extension"
                >
                  ✓ Ext Request
                </button>
                <button
                  onClick={() => handleApproveExtension(hold.id, false)}
                  className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                  title="Deny extension"
                >
                  ✗ Ext Request
                </button>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-bold text-gray-800">Admin Dashboard</h1>
          <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold">Staff Access</span>
        </div>

        {/* Today at the library — quick actions snapshot */}
        <AdminQuickActions />

        {/* Staff schedule-pickup modal */}
        {pickupModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPickupModalOpen(false)}>
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="admin-pickup-modal">
              <h3 className="text-lg font-bold text-gray-800 mb-1">Schedule a pickup</h3>
              <p className="text-sm text-gray-500 mb-4">Reserve a Thursday pickup-box slot on behalf of a member.</p>
              {pickupModalMember ? (
                <div className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-lg mb-4">
                  <div>
                    <p className="font-semibold text-gray-800">{pickupModalMember.name}</p>
                    <p className="text-xs text-gray-500">#{pickupModalMember.membership_number} {pickupModalMember.email && `• ${pickupModalMember.email}`}</p>
                  </div>
                  <button onClick={() => setPickupModalMember(null)} className="text-gray-500 hover:text-gray-700" data-testid="admin-pickup-clear-member">✕</button>
                </div>
              ) : (
                <div className="relative mb-4">
                  <input
                    value={pickupModalQuery}
                    onChange={(e) => searchPickupMembers(e.target.value)}
                    placeholder="Search member by name, email or number…"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    data-testid="admin-pickup-member-search"
                  />
                  {pickupModalResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow max-h-48 overflow-y-auto">
                      {pickupModalResults.map((m) => (
                        <button key={m.id} onClick={() => { setPickupModalMember(m); setPickupModalResults([]); }} className="w-full text-left px-3 py-2 hover:bg-teal-50 text-sm" data-testid={`admin-pickup-result-${m.id}`}>
                          <span className="font-medium">{m.name}</span> <span className="text-gray-400 text-xs">#{m.membership_number}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup date <span className="text-gray-400 font-normal">(Thursdays only)</span></label>
              <input type="date" value={pickupModalDate} onChange={(e) => setPickupModalDate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3" data-testid="admin-pickup-date" />
              <input type="text" value={pickupModalAddress} onChange={(e) => setPickupModalAddress(e.target.value)} placeholder="Member address (optional)" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-5" data-testid="admin-pickup-address" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setPickupModalOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
                <button onClick={submitAdminPickup} disabled={schedulingAdminPickup} className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50" data-testid="admin-pickup-submit">
                  {schedulingAdminPickup ? 'Scheduling…' : 'Schedule pickup'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'stats'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="stats-tab"
          >
            <FiBarChart2 className="w-4 h-4" />
            <span>Stats</span>
          </button>
          <button
            onClick={() => setActiveTab('holds')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'holds'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="manage-holds-tab"
          >
            <FiClock className="w-4 h-4" />
            <span>Manage Holds</span>
          </button>
          <button
            onClick={() => setActiveTab('addbook')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'addbook'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="add-book-tab"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Books</span>
          </button>
          <button
            onClick={() => setActiveTab('procurements')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'procurements'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="procurements-tab"
          >
            <FiBook className="w-4 h-4" />
            <span>Procurements</span>
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'users'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="users-tab"
          >
            <FiUsers className="w-4 h-4" />
            <span>Users</span>
          </button>
          <button
            onClick={() => setActiveTab('pauses')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'pauses'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="pauses-tab"
          >
            <FiPause className="w-4 h-4" />
            <span>Pauses</span>
          </button>
          <button
            onClick={() => setActiveTab('suggestions')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'suggestions'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="suggestions-tab"
          >
            <FiBook className="w-4 h-4" />
            <span>Suggestions</span>
          </button>
          <button
            onClick={() => setActiveTab('gifts')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'gifts'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="gifts-tab"
          >
            <FiGift className="w-4 h-4" />
            <span>Gifts</span>
          </button>
          <button
            onClick={() => setActiveTab('fines')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'fines'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="fines-tab"
          >
            <FiDollarSign className="w-4 h-4" />
            <span>Fines</span>
            {finesSummary && finesSummary.unpaid_count > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {finesSummary.unpaid_count}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('rejections')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'rejections'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="rejections-tab"
          >
            <FiAlertCircle className="w-4 h-4" />
            <span>Hold Rejections</span>
            {holdRejections && holdRejections.total_rejections > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {holdRejections.total_rejections}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'reviews'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="reviews-tab"
          >
            <FiMessageSquare className="w-4 h-4" />
            <span>Reviews</span>
            {pendingReviews.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {pendingReviews.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('backup')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'backup'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="backup-tab"
          >
            <FiDownload className="w-4 h-4" />
            <span>Backup</span>
          </button>
          <button
            onClick={() => setActiveTab('recommendations')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'recommendations'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="recommendations-tab"
          >
            <FiHeart className="w-4 h-4" />
            <span>Requests {recommendationRequests.length > 0 && `(${recommendationRequests.length})`}</span>
          </button>
          <button
            onClick={() => setActiveTab('addons')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'addons'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="addons-tab"
          >
            <FiPackage className="w-4 h-4" />
            <span>Add-Ons</span>
          </button>
          <button
            onClick={() => setActiveTab('coming-soon-calendar')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'coming-soon-calendar'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="coming-soon-calendar-tab"
          >
            <FiCalendar className="w-4 h-4" />
            <span>Coming Soon</span>
          </button>
          <button
            onClick={() => setActiveTab('member-actions')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 flex items-center space-x-2 whitespace-nowrap ${
              activeTab === 'member-actions'
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-600 hover:text-gray-800'
            }`}
            data-testid="member-actions-tab"
          >
            <FiBook className="w-4 h-4" />
            <span>Checkout</span>
          </button>
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <StatsTab
            dashboardStats={dashboardStats} loadingStats={loadingStats} fetchDashboardStats={fetchDashboardStats}
            borrowRules={borrowRules} loadingBorrowRules={loadingBorrowRules}
            newRuleCategory={newRuleCategory} newRuleDays={newRuleDays} addingRule={addingRule}
            editingRule={editingRule} editRuleDays={editRuleDays}
            setNewRuleCategory={setNewRuleCategory} setNewRuleDays={setNewRuleDays}
            setEditingRule={setEditingRule} setEditRuleDays={setEditRuleDays}
            handleAddBorrowRule={handleAddBorrowRule} handleUpdateBorrowRule={handleUpdateBorrowRule}
            handleDeleteBorrowRule={handleDeleteBorrowRule}
            fixingInventory={fixingInventory} handleFixAllInventory={handleFixAllInventory}
            sendingRenewalReminders={sendingRenewalReminders} handleSendRenewalReminders={handleSendRenewalReminders}
            sendingExpiredNotices={sendingExpiredNotices} handleSendExpiredNotices={handleSendExpiredNotices}
            fixingStaff={fixingStaff} handleFixStaffSubscriptions={handleFixStaffSubscriptions}
            recalculatingPayments={recalculatingPayments} handleRecalculateAdvancePayments={handleRecalculateAdvancePayments}
            getAuthHeader={getAuthHeader}
          />
        )}

        {activeTab === 'pauses' && (
          <PauseRequestsTab getAuthHeader={getAuthHeader} />
        )}

        {activeTab === 'suggestions' && (
          <BookSuggestionsTab getAuthHeader={getAuthHeader} />
        )}

        {activeTab === 'gifts' && (
          <GiftsTab getAuthHeader={getAuthHeader} />
        )}


        {/* Manage Holds Tab */}
        {activeTab === 'holds' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Manage Holds</h2>
              <div className="text-sm text-gray-600">
                <span className="block">Pickup: <span className="font-semibold text-yellow-600">Pending</span> → <span className="font-semibold text-blue-600">Reserved</span> → <span className="font-semibold text-green-600">Borrowed</span> → <span className="font-semibold text-gray-500">Returned</span></span>
                <span className="block">Delivery: <span className="font-semibold text-yellow-600">Pending</span> → <span className="font-semibold text-purple-600">Delivered</span> → <span className="font-semibold text-gray-500">Returned</span></span>
              </div>
            </div>
            
            {/* Status Filter Buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { key: 'all', label: 'All Active', bgActive: 'bg-gray-600', bgInactive: 'bg-gray-100', textActive: 'text-white', textInactive: 'text-gray-700' },
                { key: 'pending', label: 'Pending', bgActive: 'bg-yellow-600', bgInactive: 'bg-yellow-50', textActive: 'text-white', textInactive: 'text-yellow-700' },
                { key: 'queued', label: 'Queued', bgActive: 'bg-orange-600', bgInactive: 'bg-orange-50', textActive: 'text-white', textInactive: 'text-orange-700' },
                { key: 'reserved', label: 'Reserved', bgActive: 'bg-blue-600', bgInactive: 'bg-blue-50', textActive: 'text-white', textInactive: 'text-blue-700' },
                { key: 'active', label: 'Active Borrows', bgActive: 'bg-green-600', bgInactive: 'bg-green-50', textActive: 'text-white', textInactive: 'text-green-700' },
                { key: 'overdue', label: 'Overdue', bgActive: 'bg-red-600', bgInactive: 'bg-red-50', textActive: 'text-white', textInactive: 'text-red-700' },
                { key: 'extensions', label: 'Extension Requests', bgActive: 'bg-indigo-600', bgInactive: 'bg-indigo-50', textActive: 'text-white', textInactive: 'text-indigo-700' }
              ].map(({ key, label, bgActive, bgInactive, textActive, textInactive }) => {
                const counts = getStatusCounts();
                const isActive = holdStatusFilter === key;
                const count = counts[key] || 0;
                
                return (
                  <button
                    key={key}
                    onClick={() => setHoldStatusFilter(key)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      isActive
                        ? `${bgActive} ${textActive}`
                        : `${bgInactive} ${textInactive} hover:opacity-80`
                    }`}
                    data-testid={`filter-${key}`}
                  >
                    {label}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      isActive ? 'bg-white/20' : 'bg-black/10'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            {(holdStatusFilter === 'all' || holdStatusFilter === 'pending') && (
              <div data-testid="pickup-requests-panel" className="mb-6 rounded-lg border-2 border-teal-200 bg-teal-50/60 p-4">
                <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
                  <h3 className="font-semibold text-teal-800">📦 Scheduled pickup requests ({scheduledPickupReqs.length})</h3>
                  <button onClick={() => openPickupModal()} className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700" data-testid="open-admin-pickup-modal">+ Schedule pickup</button>
                </div>
                <p className="text-sm text-gray-500 mb-3">Members who reserved a Thursday slot to collect their books from the pickup box.</p>
                {scheduledPickupReqs.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No pickup requests scheduled.</p>
                ) : (
                <div className="overflow-x-auto">
                  <table className="w-full bg-white rounded">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2.5 px-4 text-gray-600 font-semibold text-sm">Member</th>
                        <th className="text-left py-2.5 px-4 text-gray-600 font-semibold text-sm">Pickup Date</th>
                        <th className="text-left py-2.5 px-4 text-gray-600 font-semibold text-sm">Contact</th>
                        <th className="text-left py-2.5 px-4 text-gray-600 font-semibold text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledPickupReqs.map((p) => (
                        <tr key={p.id} className="border-b hover:bg-gray-50" data-testid={`pickup-req-${p.id}`}>
                          <td className="py-2.5 px-4 text-gray-800">
                            <span className="font-medium">{p.user?.name || 'Unknown'}</span>
                            <p className="text-xs text-gray-500">{p.user?.membership_number}</p>
                          </td>
                          <td className="py-2.5 px-4 text-gray-800 font-medium">
                            {new Date(p.pickup_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                            {p.address && <p className="text-xs text-gray-500 font-normal">{p.address}</p>}
                          </td>
                          <td className="py-2.5 px-4 text-sm text-gray-600">
                            <span className="block">{p.user?.phone || '—'}</span>
                            <span className="block text-xs text-gray-400">{p.user?.email}</span>
                          </td>
                          <td className="py-2.5 px-4">
                            <div className="flex gap-2">
                              <button onClick={() => handlePickupReqAction(p.id, false)} className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700" data-testid={`pickup-req-done-${p.id}`}>✓ Done</button>
                              <button onClick={() => handlePickupReqAction(p.id, true)} className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600" data-testid={`pickup-req-cancel-${p.id}`}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {loading ? (
              <p className="text-gray-600">Loading holds...</p>
            ) : getFilteredHolds().length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FiClock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No holds found for this filter.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Member</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Book</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Requested Pickup</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Due Date</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Status</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredHolds().map((hold) => {
                      const holdIsOverdue = isOverdue(hold);
                      const hasExtensionRequest = hold.extension_requested && hold.extension_approved === null;
                      const isPreorder = hold.is_preorder || hold.book_available_from;
                      const hasRequestedPickup = hold.requested_pickup_date && new Date(hold.requested_pickup_date) > new Date(hold.hold_date);
                      
                      return (
                        <tr 
                          key={hold.id} 
                          className={`border-b hover:bg-gray-50 ${holdIsOverdue ? 'bg-red-50' : ''} ${hasExtensionRequest ? 'bg-purple-50' : ''} ${isPreorder ? 'bg-amber-50' : ''}`}
                          data-testid={`admin-hold-${hold.id}`}
                        >
                          <td className="py-3 px-4 text-gray-800">
                            <div>
                              <button
                                onClick={() => hold.user && handleUserClick(hold.user)}
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                                data-testid={`member-link-${hold.id}`}
                              >
                                {hold.user?.name || 'Unknown'}{hold.is_child_hold ? ` (${hold.child_name || 'Child'} — Children membership)` : ''}
                              </button>
                              <p className="text-xs text-gray-500">{hold.user?.membership_number}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            <div>
                              <button
                                onClick={() => hold.book && handleBookClick(hold.book)}
                                className="font-medium text-amber-700 hover:text-amber-900 hover:underline text-left"
                                data-testid={`book-link-${hold.id}`}
                              >
                                {hold.book?.title || 'Unknown'}
                              </button>
                              <p className="text-sm text-gray-500">{hold.book?.authors?.join(', ')}</p>
                              {isPreorder && (
                                <p className="text-xs text-amber-700 font-medium mt-1">
                                  📅 Available from: {new Date(hold.book_available_from).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            <div>
                              {hold.requested_pickup_date ? (
                                <>
                                  <p className={`font-medium ${hasRequestedPickup ? 'text-blue-600' : ''}`}>
                                    {new Date(hold.requested_pickup_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </p>
                                  {hasRequestedPickup && (
                                    <p className="text-xs text-gray-500">
                                      (held on {new Date(hold.hold_date).toLocaleDateString()})
                                    </p>
                                  )}
                                </>
                              ) : (
                                <p className="text-gray-500">{new Date(hold.hold_date).toLocaleDateString()}</p>
                              )}
                              {/* Delivery/Pickup Badge */}
                              <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                                hold.delivery_option === 'paid_delivery' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {hold.delivery_option === 'paid_delivery' ? '🚚 Delivery' : '🏠 Self Pickup'}
                              </span>
                              {/* Delivery address (only for paid_delivery) */}
                              {hold.delivery_option === 'paid_delivery' && hold.delivery_address && (
                                <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-900" data-testid={`delivery-address-${hold.id}`}>
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 whitespace-pre-wrap break-words">
                                      <span className="font-semibold">🚚 Deliver to:</span> {hold.delivery_address}
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const btn = e.currentTarget;
                                        const orig = btn.innerText;
                                        navigator.clipboard.writeText(hold.delivery_address).then(() => {
                                          if (!btn) return;
                                          btn.innerText = 'Copied!';
                                          setTimeout(() => { if (btn) btn.innerText = orig; }, 1200);
                                        }).catch(() => {});
                                      }}
                                      className="flex-shrink-0 px-2 py-0.5 text-[10px] font-semibold bg-blue-600 text-white rounded hover:bg-blue-700"
                                      title="Copy address"
                                      data-testid={`copy-delivery-address-${hold.id}`}
                                    >
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            {hold.status === 'returned' ? (
                              <div>
                                <span className="text-gray-500">Returned</span>
                                {hold.return_date && (
                                  <p className="text-xs text-gray-600">
                                    {new Date(hold.return_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            ) : hold.due_date ? (
                              <div>
                                <span className={holdIsOverdue ? 'text-red-600 font-semibold' : 'font-medium'}>
                                  {new Date(hold.due_date).toLocaleDateString()}
                                </span>
                                {holdIsOverdue && (
                                  <p className="text-xs text-red-600">
                                    {Math.ceil((new Date() - new Date(hold.due_date)) / (1000 * 60 * 60 * 24))} days overdue
                                  </p>
                                )}
                                {!holdIsOverdue && ['reserved', 'borrowed', 'delivered'].includes(hold.status) && (
                                  <p className="text-xs text-gray-500">
                                    {Math.ceil((new Date(hold.due_date) - new Date()) / (1000 * 60 * 60 * 24))} days left
                                  </p>
                                )}
                                {hasExtensionRequest && (
                                  <p className="text-xs text-purple-600 font-medium">
                                    +{hold.extension_requested_days || 7}d requested
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">Not yet set</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-col gap-1">
                              {holdIsOverdue ? (
                                <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                                  Overdue
                                </span>
                              ) : (
                                getStatusBadge(hold.status)
                              )}
                              {isPreorder && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  Pre-order
                                </span>
                              )}
                              {hold.hold_type === 'rental' && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700">
                                  Rental
                                </span>
                              )}
                              {hasExtensionRequest && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                  Extension Pending
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {getHoldActions(hold)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Returned holds section */}
            {holdStatusFilter === 'all' && allHolds.filter(h => h.status === 'returned').length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Recently Returned</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 text-gray-500 font-medium text-sm">Member</th>
                        <th className="text-left py-2 px-4 text-gray-500 font-medium text-sm">Book</th>
                        <th className="text-left py-2 px-4 text-gray-500 font-medium text-sm">Returned</th>
                        <th className="text-right py-2 px-4 text-gray-500 font-medium text-sm">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allHolds
                        .filter(h => h.status === 'returned')
                        .slice(0, 10)
                        .map((hold) => (
                          <tr key={hold.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-4 text-sm">
                              <button
                                onClick={() => hold.user && handleUserClick(hold.user)}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {hold.user?.name || 'Unknown'}{hold.is_child_hold ? ` (${hold.child_name || 'Child'} — Children membership)` : ''}
                              </button>
                            </td>
                            <td className="py-2 px-4 text-sm">
                              <button
                                onClick={() => hold.book && handleBookClick(hold.book)}
                                className="text-amber-700 hover:text-amber-900 hover:underline"
                              >
                                {hold.book?.title || 'Unknown'}
                              </button>
                            </td>
                            <td className="py-2 px-4 text-gray-600 text-sm">
                              {hold.return_date ? new Date(hold.return_date).toLocaleDateString() : '—'}
                            </td>
                            <td className="py-2 px-4 text-right">
                              <button
                                onClick={() => handleUndoReturn(hold)}
                                className="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold rounded border border-amber-300"
                                data-testid={`undo-return-${hold.id}`}
                                title="Marked as returned by mistake? Revert it."
                              >Undo return</button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Books Tab */}
        {activeTab === 'addbook' && (
          <AddBookTab
            addBookMode={addBookMode} setAddBookMode={setAddBookMode}
            isbn={isbn} setIsbn={setIsbn} scanning={scanning} scanResult={scanResult}
            handleScanISBN={handleScanISBN} manualBook={manualBook} setManualBook={setManualBook}
            addingBook={addingBook} bookResult={bookResult} handleAddBook={handleAddBook}
            navigate={navigate}
            selectedAuthors={selectedAuthors} setSelectedAuthors={setSelectedAuthors}
            selectedCategories={selectedCategories} setSelectedCategories={setSelectedCategories}
            authorInput={authorInput} setAuthorInput={setAuthorInput}
            categoryInput={categoryInput} setCategoryInput={setCategoryInput}
            showAuthorDropdown={showAuthorDropdown} setShowAuthorDropdown={setShowAuthorDropdown}
            showCategoryDropdown={showCategoryDropdown} setShowCategoryDropdown={setShowCategoryDropdown}
            allAuthors={allAuthors} allCategories={allCategories}
            authorDropdownRef={authorDropdownRef} categoryDropdownRef={categoryDropdownRef}
            vendorInput={vendorInput} setVendorInput={setVendorInput}
            showVendorDropdown={showVendorDropdown} setShowVendorDropdown={setShowVendorDropdown}
            allVendors={allVendors} vendorDropdownRef={vendorDropdownRef}
            getAuthHeader={getAuthHeader} fetchAuthorsAndCategories={fetchAuthorsAndCategories}
          />
        )}



        {/* All Borrows Tab */}
        {activeTab === 'borrows' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">All Active Borrows</h2>
            
            {loading ? (
              <p className="text-gray-600">Loading...</p>
            ) : allBorrows.filter(b => b.status === 'borrowed').length === 0 ? (
              <p className="text-gray-600">No active borrows.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Member</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Book</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Borrowed</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Due Date</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBorrows
                      .filter(b => b.status === 'borrowed')
                      .map((borrow) => (
                        <tr key={borrow.id} className="border-b hover:bg-gray-50" data-testid={`admin-borrow-${borrow.id}`}>
                          <td className="py-3 px-4 text-gray-800">{borrow.user?.name || 'Unknown'}</td>
                          <td className="py-3 px-4 text-gray-800">{borrow.book?.title || 'Unknown'}</td>
                          <td className="py-3 px-4 text-gray-800">
                            {new Date(borrow.borrow_date).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            {new Date(borrow.due_date).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => handleReturnBook(borrow.id, borrow.book?.title)}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
                              data-testid={`admin-return-button-${borrow.id}`}
                            >
                              Mark Returned
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Procurements Tab */}
        {activeTab === 'procurements' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Procurement Records</h2>

            {/* Reorder Suggestions — books frequently being rejected at hold-time */}
            {holdRejections && Array.isArray(holdRejections.by_book) && holdRejections.by_book.filter(b => (b.count || 0) >= 5).length > 0 && (
              <div className="mb-8 border-2 border-red-200 bg-red-50/40 rounded-xl p-5" data-testid="reorder-suggestions">
                <div className="flex items-start gap-3 mb-3">
                  <FiAlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-red-800">Reorder suggestions</h3>
                    <p className="text-sm text-gray-700">
                      These books have been requested but blocked <strong>5+ times</strong> in the last 30 days. Consider ordering more copies.
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-100 border-b border-red-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold text-red-900">Book</th>
                        <th className="text-right px-4 py-2 font-semibold text-red-900">Rejections (30d)</th>
                        <th className="text-right px-4 py-2 font-semibold text-red-900">Current copies</th>
                        <th className="text-left px-4 py-2 font-semibold text-red-900">Last attempted</th>
                        <th className="text-right px-4 py-2 font-semibold text-red-900">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {holdRejections.by_book
                        .filter(b => (b.count || 0) >= 5)
                        .slice(0, 20)
                        .map((b, idx) => (
                          <tr key={(b.book_id || 'x') + '_' + idx} className="border-b border-red-100 hover:bg-red-50">
                            <td className="px-4 py-2">
                              {b.book_id ? (
                                <button
                                  onClick={() => navigate(`/book/${b.book_id}`)}
                                  className="text-amber-700 hover:text-amber-900 hover:underline font-medium text-left"
                                  data-testid={`reorder-suggestion-link-${idx}`}
                                >
                                  {b.book_title}
                                </button>
                              ) : (
                                <span className="text-gray-800">{b.book_title}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <span className="inline-block px-2 py-0.5 bg-red-200 text-red-800 rounded font-bold">{b.count}</span>
                            </td>
                            <td className="px-4 py-2 text-right text-gray-700">
                              {b.total_copies != null ? (
                                <span>{b.available_copies ?? '?'} of {b.total_copies}</span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-600">
                              {b.last_attempted ? new Date(b.last_attempted).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {b.book_id && (
                                <button
                                  onClick={() => navigate(`/book/${b.book_id}`)}
                                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg"
                                  data-testid={`reorder-suggestion-action-${idx}`}
                                >
                                  Add copy
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-amber-700">Total Procured</p>
                <p className="text-2xl font-bold text-amber-900">{procurements.summary?.total_procured || 0}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-700">Purchased</p>
                <p className="text-2xl font-bold text-green-900">{procurements.summary?.purchased || 0}</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-700">Donated</p>
                <p className="text-2xl font-bold text-blue-900">{procurements.summary?.donated || 0}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-700">Total Cost</p>
                <p className="text-2xl font-bold text-purple-900">₹{(procurements.summary?.total_cost || 0).toLocaleString()}</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-4">
              To add procurement info, go to any book's detail page and click "Edit Book" → Procurement Information.
            </p>
            
            {loadingProcurements ? (
              <p className="text-gray-600">Loading procurement records...</p>
            ) : procurements.books?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FiBook className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No procurement records yet.</p>
                <p className="text-sm mt-2">Add procurement info when editing books in the catalog.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Book Title</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Type</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Date</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Source</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {procurements.books?.map((book) => (
                      <tr key={book.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-gray-800">{book.title}</p>
                          <p className="text-sm text-gray-500">{book.authors?.join(', ')}</p>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            book.procurement_type === 'purchased' ? 'bg-green-100 text-green-700' :
                            book.procurement_type === 'donated' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {book.procurement_type || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{book.procurement_date || '—'}</td>
                        <td className="py-3 px-4 text-gray-600">{book.procurement_source || '—'}</td>
                        <td className="py-3 px-4 text-gray-800 font-medium">
                          {book.procurement_price ? `₹${book.procurement_price.toLocaleString()}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Download members + borrowing history */}
            <div className="flex justify-end mb-4">
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/admin/export/members-with-borrows`, {
                      headers: getAuthHeader(),
                      responseType: 'blob',
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                    const a = document.createElement('a');
                    a.href = url;
                    const ts = new Date().toISOString().slice(0, 10);
                    a.download = `members_with_borrows_${ts}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    alert(err.response?.data?.detail || 'Failed to download CSV');
                  }
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2"
                data-testid="download-members-csv-btn"
              >
                <FiDownload className="w-4 h-4" />
                Download members + borrowing history (.csv)
              </button>
            </div>

            {/* Subscription Reminders - Prominent Section */}
            <div className="mb-8 p-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-xl">📧</span> Send Subscription Reminders
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Send reminder emails to members who signed up but haven't activated their subscription yet.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/admin/inactive-subscribers?days=7`, { headers: getAuthHeader() });
                      const members = response.data.members || [];
                      if (members.length === 0) {
                        alert('No inactive members found (7+ days without subscription)');
                        return;
                      }
                      const list = members.slice(0, 15).map(m => `• ${m.name} (${m.email}) - Joined: ${new Date(m.created_at).toLocaleDateString()}`).join('\n');
                      const more = members.length > 15 ? `\n... and ${members.length - 15} more` : '';
                      alert(`Inactive Members (7+ days without subscription):\n\n${list}${more}\n\nTotal: ${members.length}`);
                    } catch (error) {
                      alert(error.response?.data?.detail || 'Failed to fetch inactive members');
                    }
                  }}
                  className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium border border-gray-300 shadow-sm"
                  data-testid="view-inactive-users"
                >
                  👁️ View Inactive Members
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/admin/inactive-subscribers?days=7`, { headers: getAuthHeader() });
                      const count = response.data.count;
                      if (count === 0) {
                        alert('No inactive members found (7+ days without subscription)');
                        return;
                      }
                      if (!window.confirm(`Found ${count} members who joined 7+ days ago without subscribing.\n\nSend subscription reminder emails to all of them?`)) return;
                      
                      const sendResponse = await axios.post(`${API}/admin/send-subscription-reminders?days=7`, {}, { headers: getAuthHeader() });
                      alert(`✅ ${sendResponse.data.message}`);
                    } catch (error) {
                      alert(error.response?.data?.detail || 'Failed to send reminders');
                    }
                  }}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium shadow-sm"
                  data-testid="send-subscription-reminders-users"
                >
                  📬 Send Reminder Emails
                </button>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-4">All Users</h2>
            
            {/* Subscription Filter */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setUserSubscriptionFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  userSubscriptionFilter === 'all'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All ({users.length})
              </button>
              <button
                onClick={() => setUserSubscriptionFilter('active')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  userSubscriptionFilter === 'active'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                }`}
              >
                Active ({users.filter(u => u.role !== 'staff' && u.subscription_status === 'active').length})
              </button>
              <button
                onClick={() => setUserSubscriptionFilter('inactive')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  userSubscriptionFilter === 'inactive'
                    ? 'bg-red-600 text-white'
                    : 'bg-red-50 text-red-700 hover:bg-red-100'
                }`}
              >
                Inactive ({users.filter(u => u.role !== 'staff' && u.subscription_status !== 'active').length})
              </button>
              <button
                onClick={() => setUserSubscriptionFilter('expired')}
                data-testid="users-filter-expired"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  userSubscriptionFilter === 'expired'
                    ? 'bg-orange-600 text-white'
                    : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                }`}
              >
                Expired ({users.filter(u => u.role !== 'staff' && u.subscription_end_date && new Date(u.subscription_end_date) < new Date()).length})
              </button>
            </div>
            
            {loading ? (
              <p className="text-gray-600">Loading...</p>
            ) : users.length === 0 ? (
              <p className="text-gray-600">No users found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Member ID</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Name</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Email</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Phone</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Role</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Subscription</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Joined</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-semibold">Membership</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users
                      .filter(user => {
                        if (userSubscriptionFilter === 'all') return true;
                        if (user.role === 'staff') return false;
                        if (userSubscriptionFilter === 'active') return user.subscription_status === 'active';
                        if (userSubscriptionFilter === 'inactive') return user.subscription_status !== 'active';
                        if (userSubscriptionFilter === 'expired') return user.subscription_end_date && new Date(user.subscription_end_date) < new Date();
                        return true;
                      })
                      .map((user) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50" data-testid={`admin-user-${user.id}`}>
                        <td className="py-3 px-4 font-mono text-sm text-amber-700">{user.membership_number || '—'}</td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleUserClick(user)}
                            className="text-amber-600 hover:text-amber-800 hover:underline font-medium text-left"
                            data-testid={`user-name-${user.id}`}
                          >
                            {user.name}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-gray-800">{user.email}</td>
                        <td className="py-3 px-4 text-gray-800">{user.phone || '—'}</td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            user.role === 'staff' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            user.subscription_status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {user.subscription_status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-800 text-sm">
                          {new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {user.subscription_start_date || user.subscription_end_date ? (
                            <div className="space-y-1">
                              {user.subscription_start_date && (
                                <div className="text-gray-600">
                                  <span className="text-green-600 font-medium">Start:</span> {new Date(user.subscription_start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                              {user.subscription_end_date && (
                                <div className={`${
                                  new Date(user.subscription_end_date) < new Date() 
                                    ? 'text-red-600 font-medium' 
                                    : 'text-gray-600'
                                }`}>
                                  <span className={new Date(user.subscription_end_date) < new Date() ? 'text-red-600' : 'text-orange-600'}>Expiry:</span> {new Date(user.subscription_end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">Never subscribed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* User Details Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-amber-50">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{selectedUser.name}</h2>
                  <p className="text-sm text-amber-700 font-mono">{selectedUser.membership_number}</p>
                </div>
                <button
                  onClick={closeUserModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                {loadingUserDetails ? (
                  <p className="text-gray-600 text-center py-8">Loading user details...</p>
                ) : userDetails ? (
                  <div className="space-y-6">
                    {/* Contact Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">Contact Information</h3>
                        {!editingMember ? (
                          <div className="flex gap-2">
                          <button
                            onClick={handleToggleStaff}
                            className={`px-3 py-1 text-sm rounded-lg transition-colors ${userDetails.user.role === 'staff' ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'}`}
                            data-testid="toggle-staff-btn"
                          >
                            {userDetails.user.role === 'staff' ? '👤 Remove staff access' : '👔 Make staff'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingMember(true);
                              setEditMemberData({
                                name: userDetails.user.name || '',
                                email: userDetails.user.email || '',
                                phone: userDetails.user.phone || ''
                              });
                            }}
                            className="px-3 py-1 text-sm bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg flex items-center gap-1 transition-colors"
                            data-testid="edit-member-btn"
                          >
                            <FiEdit className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveMemberProfile(userDetails.user.id)}
                              disabled={savingMember}
                              className="px-3 py-1 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg disabled:opacity-50"
                              data-testid="save-member-btn"
                            >
                              {savingMember ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => setEditingMember(false)}
                              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-lg"
                              data-testid="cancel-edit-member-btn"
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>

                      {editingMember ? (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Name</label>
                            <input
                              type="text"
                              value={editMemberData.name}
                              onChange={(e) => setEditMemberData({...editMemberData, name: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                              data-testid="edit-member-name"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Email</label>
                              <input
                                type="email"
                                value={editMemberData.email}
                                onChange={(e) => setEditMemberData({...editMemberData, email: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                data-testid="edit-member-email"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Phone</label>
                              <input
                                type="text"
                                value={editMemberData.phone}
                                onChange={(e) => setEditMemberData({...editMemberData, phone: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                data-testid="edit-member-phone"
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <FiMail className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">{userDetails.user.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiPhone className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700">{userDetails.user.phone || 'Not provided'}</span>
                        </div>
                      </div>
                      )}
                      <div className="mt-3 flex gap-4 text-sm items-center flex-wrap">
                        <span className={`px-2 py-1 rounded ${userDetails.user.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {userDetails.user.subscription_status}
                        </span>
                        {userDetails.user.subscription_plan && (
                          <span className="text-gray-500">
                            Plan: {userDetails.user.subscription_plan}
                          </span>
                        )}
                        <span className="text-gray-500">
                          Joined: {new Date(userDetails.user.created_at).toLocaleDateString()}
                        </span>
                        {userDetails.user.outstanding_fees > 0 && (
                          <span className="text-red-600 font-semibold">
                            Owes: ₹{userDetails.user.outstanding_fees.toFixed(2)}
                          </span>
                        )}
                      </div>

                      {userDetails.user.role === 'member' && (
                        <div className="mt-4">
                          <DeliveryBalancePanel
                            userId={userDetails.user.id}
                            currentBalance={userDetails.user.delivery_balance || 0}
                            getAuthHeader={getAuthHeader}
                            onChanged={() => fetchUserDetails(userDetails.user.id)}
                          />
                        </div>
                      )}
                      
                      {/* Subscription Dates Display */}
                      {userDetails.user.role === 'member' && (userDetails.user.subscription_start_date || userDetails.user.subscription_end_date) && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex flex-wrap gap-4 text-sm">
                            {userDetails.user.subscription_start_date && (
                              <div>
                                <span className="text-gray-600">Started:</span>{' '}
                                <span className="font-medium text-green-700">
                                  {new Date(userDetails.user.subscription_start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              </div>
                            )}
                            {userDetails.user.subscription_end_date && (
                              <div>
                                <span className="text-gray-600">Expires:</span>{' '}
                                <span className={`font-medium ${new Date(userDetails.user.subscription_end_date) < new Date() ? 'text-red-600' : 'text-orange-600'}`}>
                                  {new Date(userDetails.user.subscription_end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Adjust Subscription End Date */}
                          <div className="mt-3 pt-3 border-t border-amber-200">
                            <label className="block text-xs text-gray-600 mb-1">Adjust Expiry Date (for advance payment corrections):</label>
                            <div className="flex gap-2 items-center">
                              <input
                                type="date"
                                value={newSubscriptionEndDate}
                                onChange={(e) => setNewSubscriptionEndDate(e.target.value)}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                                min={new Date().toISOString().split('T')[0]}
                              />
                              <button
                                onClick={() => handleAdjustSubscription(userDetails.user.id)}
                                disabled={adjustingSubscription || !newSubscriptionEndDate}
                                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm disabled:opacity-50"
                                data-testid="adjust-subscription-btn"
                              >
                                {adjustingSubscription ? 'Saving...' : 'Update'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {userDetails.user.role === 'member' && (() => {
                        const u = userDetails.user;
                        const isChildOnly = u.membership_type === 'children';
                        const adultSeats = isChildOnly ? 0 : (u.adult_count || 1);
                        const childSeats = u.child_count || ((u.child_membership_status || isChildOnly) ? 1 : 0);
                        const childStatus = isChildOnly ? u.subscription_status : u.child_membership_status;
                        const chip = (s) => (s === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600');
                        return (
                          <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200" data-testid="family-snapshot">
                            <p className="text-sm font-semibold text-indigo-900 mb-2">👨‍👩‍👧 Family snapshot</p>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              {adultSeats > 0 && (
                                <span className="bg-white border border-indigo-200 rounded-full px-3 py-1" data-testid="family-adult-seats">
                                  Adult × <strong>{adultSeats}</strong>
                                  <span className={`ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 ${chip(u.subscription_status)}`}>{u.subscription_status || 'none'}</span>
                                </span>
                              )}
                              <span className="bg-white border border-indigo-200 rounded-full px-3 py-1" data-testid="family-child-seats">
                                Child × <strong>{childSeats}</strong>
                                {childSeats > 0 && u.child_name && <span className="text-gray-500"> — {u.child_name}</span>}
                                {childSeats > 0 && <span className={`ml-2 text-[11px] font-semibold rounded-full px-2 py-0.5 ${chip(childStatus)}`}>{childStatus || 'none'}</span>}
                              </span>
                              <span className="text-xs text-gray-500" data-testid="family-book-limit">
                                Book limit: {adultSeats > 0 ? `${adultSeats * 5} adult` : ''}{adultSeats > 0 && childSeats > 0 ? ' + ' : ''}{childSeats > 0 ? `${childSeats * 5} kids'` : ''}
                              </span>
                            </div>
                          </div>
                        );
                      })()}

                      {userDetails.user.role === 'member' && (
                        <PaymentHistoryPanel
                          userId={userDetails.user.id}
                          payments={userDetails.payments || []}
                          getAuthHeader={getAuthHeader}
                          onChanged={() => fetchUserDetails(userDetails.user.id)}
                        />
                      )}
                      
                      {/* Subscription Management - Only for members */}
                      {userDetails.user.role === 'member' && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => {
                                setSubscriptionUpdate({
                                  status: userDetails.user.subscription_status === 'active' ? 'inactive' : 'active',
                                  plan: 'cash',
                                  duration: 1,
                                  note: ''
                                });
                                setShowSubscriptionModal(true);
                              }}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                userDetails.user.subscription_status === 'active'
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                              data-testid="toggle-subscription-btn"
                            >
                              {userDetails.user.subscription_status === 'active' ? '⏸️ Deactivate' : '✅ Activate'} Subscription
                            </button>
                            {userDetails.user.payment_notes && userDetails.user.payment_notes.length > 0 && (
                              <button
                                onClick={() => {
                                  const notes = userDetails.user.payment_notes
                                    .map(n => `• ${new Date(n.date).toLocaleDateString()}: ${n.note} (by ${n.updated_by_name || 'Staff'})`)
                                    .join('\n');
                                  alert(`Payment Notes:\n\n${notes}`);
                                }}
                                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                              >
                                📝 View Notes ({userDetails.user.payment_notes.length})
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-blue-600">{userDetails.stats.total_borrowed}</p>
                        <p className="text-sm text-blue-700">Total Borrowed</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-green-600">{userDetails.stats.currently_reading_count}</p>
                        <p className="text-sm text-green-700">Currently Reading</p>
                      </div>
                      <div className="bg-yellow-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-yellow-600">{userDetails.stats.pending_holds_count}</p>
                        <p className="text-sm text-yellow-700">Pending Holds</p>
                      </div>
                    </div>

                    {/* Stuck pending_payment rentals — admin clear */}
                    {userDetails.stats.stuck_rentals_count > 0 && (
                      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-center justify-between" data-testid="stuck-rentals-banner">
                        <div className="flex items-center gap-3">
                          <FiAlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-red-800">
                              {userDetails.stats.stuck_rentals_count} stuck rental {userDetails.stats.stuck_rentals_count === 1 ? 'order' : 'orders'}
                            </p>
                            <p className="text-xs text-red-700">Abandoned Razorpay checkouts — blocking new rentals for the same book.</p>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Clear ${userDetails.stats.stuck_rentals_count} stuck rental order(s) for this user?`)) return;
                            try {
                              const resp = await axios.delete(`${API}/admin/users/${userDetails.user.id}/stuck-rentals`, { headers: getAuthHeader() });
                              alert(`Cleared ${resp.data.cleared} stuck rental order(s).`);
                              fetchUserDetails(userDetails.user.id);
                            } catch (error) {
                              alert(error.response?.data?.detail || 'Failed to clear stuck rentals');
                            }
                          }}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg flex-shrink-0"
                          data-testid="clear-stuck-rentals-btn"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    {/* Currently Reading */}
                    {userDetails.currently_reading.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Currently Reading</h3>
                        <div className="space-y-2">
                          {userDetails.currently_reading.map((hold) => (
                            <div key={hold.id} className="bg-green-50 rounded-lg p-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2" data-testid={`user-current-reading-${hold.id}`}>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-800 truncate">{hold.book?.title || 'Unknown'}</p>
                                <p className="text-sm text-gray-600 truncate">{hold.book?.authors?.join(', ')}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Due: {hold.due_date ? new Date(hold.due_date).toLocaleDateString() : '—'}
                                  {hold.due_date && new Date(hold.due_date) < new Date() && (
                                    <span className="ml-2 text-red-600 font-semibold">OVERDUE</span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Return "${hold.book?.title}"?`)) return;
                                    try {
                                      await axios.put(`${API}/holds/${hold.id}/return`, {}, { headers: getAuthHeader() });
                                      alert('Book returned successfully!');
                                      fetchUserDetails(userDetails.user.id);
                                    } catch (error) {
                                      alert(error.response?.data?.detail || 'Failed to return book');
                                    }
                                  }}
                                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg"
                                  data-testid={`user-return-book-${hold.id}`}
                                >
                                  Return
                                </button>
                                <button
                                  onClick={async () => {
                                    const days = window.prompt(`Extend "${hold.book?.title}" by how many days?`, '7');
                                    if (!days) return;
                                    const numDays = parseInt(days, 10);
                                    if (!Number.isFinite(numDays) || numDays <= 0) {
                                      alert('Please enter a positive number of days.');
                                      return;
                                    }
                                    try {
                                      const resp = await axios.post(`${API}/staff/extend-loan`, {
                                        hold_id: hold.id,
                                        days: numDays
                                      }, { headers: getAuthHeader() });
                                      alert(`Extended! New due date: ${new Date(resp.data.new_due_date).toLocaleDateString()}`);
                                      fetchUserDetails(userDetails.user.id);
                                    } catch (error) {
                                      alert(error.response?.data?.detail || 'Failed to extend loan');
                                    }
                                  }}
                                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg"
                                  data-testid={`user-extend-loan-${hold.id}`}
                                >
                                  Extend
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pending Holds */}
                    {userDetails.pending_holds.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Pending Holds</h3>
                        <div className="space-y-2">
                          {userDetails.pending_holds.map((hold) => {
                            const advanceMap = { queued: 'pending', pending: 'reserved', reserved: 'borrowed', delivered: 'borrowed' };
                            const nextStatus = advanceMap[hold.status];
                            return (
                              <div key={hold.id} className="bg-yellow-50 rounded-lg p-3 flex justify-between items-center gap-2" data-testid={`user-modal-hold-${hold.id}`}>
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-gray-800 truncate">{hold.book?.title || 'Unknown'}</p>
                                  <p className="text-sm text-gray-600 truncate">{hold.book?.authors?.join(', ')}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${
                                    hold.status === 'reserved' ? 'bg-blue-100 text-blue-700' :
                                    hold.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    hold.status === 'queued' ? 'bg-gray-100 text-gray-700' :
                                    'bg-purple-100 text-purple-700'
                                  }`}>
                                    {hold.status}
                                  </span>
                                  {nextStatus && (
                                    <button
                                      onClick={() => handleAdvanceHoldStatus(hold)}
                                      className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded"
                                      data-testid={`user-modal-advance-${hold.id}`}
                                      title={`Move to "${nextStatus}"`}
                                    >→ {nextStatus}</button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Borrowing History */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Borrowing History ({userDetails.history.length})</h3>
                      {userDetails.history.length === 0 ? (
                        <p className="text-gray-500 text-sm">No borrowing history yet.</p>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {userDetails.history.map((hold) => (
                            <div key={hold.id} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-800 truncate">{hold.book?.title || 'Unknown'}</p>
                                <p className="text-sm text-gray-600 truncate">{hold.book?.authors?.join(', ')}</p>
                              </div>
                              <div className="text-right text-sm text-gray-500 shrink-0">
                                <p>Returned: {hold.return_date ? new Date(hold.return_date).toLocaleDateString() : '—'}</p>
                                {hold.status === 'returned' && (
                                  <button
                                    onClick={() => handleUndoReturn(hold)}
                                    className="mt-1 px-2 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold rounded border border-amber-300"
                                    data-testid={`user-modal-undo-return-${hold.id}`}
                                    title="Marked as returned by mistake? Revert it."
                                  >Undo return</button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Unable to load user details.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Book Library Card Modal */}
        {bookLibraryCard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={closeBookModal}>
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white p-6">
                <div className="flex justify-between items-start">
                  <div className="flex gap-4">
                    {bookLibraryCard.book.cover_image_url ? (
                      <img 
                        src={bookLibraryCard.book.cover_image_url} 
                        alt={bookLibraryCard.book.title}
                        className="w-20 h-28 object-cover rounded shadow-lg"
                      />
                    ) : (
                      <div className="w-20 h-28 bg-amber-500 rounded shadow-lg flex items-center justify-center">
                        <FiBook className="w-8 h-8 text-amber-200" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-bold">{bookLibraryCard.book.title}</h2>
                      <p className="text-amber-100">{bookLibraryCard.book.authors?.join(', ')}</p>
                      <p className="text-sm text-amber-200 mt-1">ISBN: {bookLibraryCard.book.isbn || 'N/A'}</p>
                      <div className="flex gap-2 mt-2 flex-wrap items-center">
                        <span className="px-2 py-1 bg-white/20 rounded text-xs">
                          {bookLibraryCard.book.available_copies}/{bookLibraryCard.book.total_copies} available
                        </span>
                        <span className="px-2 py-1 bg-white/20 rounded text-xs">
                          Borrowed {bookLibraryCard.total_times_borrowed} times
                        </span>
                        {bookLibraryCard.book.is_active === false && (
                          <span
                            className="px-2 py-1 bg-yellow-100 text-yellow-900 rounded text-xs font-semibold"
                            data-testid="book-hidden-badge"
                          >
                            Hidden from catalog
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <a
                          href={`/book/${bookLibraryCard.book.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium"
                          data-testid="open-book-page"
                        >
                          Open book page ↗
                        </a>
                        <button
                          onClick={async () => {
                            if (togglingVisibility) return;
                            const hiding = bookLibraryCard.book.is_active !== false;
                            if (hiding && !window.confirm(`Hide "${bookLibraryCard.book.title}" from catalog?`)) return;
                            setTogglingVisibility(true);
                            try {
                              const res = await axios.post(
                                `${API}/books/${bookLibraryCard.book.id}/toggle-active`,
                                {},
                                { headers: getAuthHeader() }
                              );
                              const newActive = res.data?.is_active ?? !hiding;
                              setBookLibraryCard({
                                ...bookLibraryCard,
                                book: { ...bookLibraryCard.book, is_active: newActive },
                              });
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to toggle visibility');
                            } finally {
                              setTogglingVisibility(false);
                            }
                          }}
                          disabled={togglingVisibility}
                          className={`px-3 py-1 rounded text-xs font-semibold disabled:opacity-50 ${
                            bookLibraryCard.book.is_active === false
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-yellow-400 hover:bg-yellow-500 text-yellow-900'
                          }`}
                          data-testid="toggle-book-visibility"
                        >
                          {togglingVisibility
                            ? '…'
                            : bookLibraryCard.book.is_active === false
                              ? 'Show in catalog'
                              : 'Hide from catalog'}
                        </button>
                      </div>
                    </div>
                  </div>
                  <button onClick={closeBookModal} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* Current Borrowers — one row per copy that's out */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FiUser className="w-5 h-5 text-amber-600" />
                    Current Borrowers ({(bookLibraryCard.current_borrowers || (bookLibraryCard.current_borrower ? [bookLibraryCard.current_borrower] : [])).length})
                  </h3>
                  {(() => {
                    const borrowers = bookLibraryCard.current_borrowers
                      || (bookLibraryCard.current_borrower ? [bookLibraryCard.current_borrower] : []);
                    if (borrowers.length === 0) {
                      return (
                        <p className="text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                          📚 All copies currently available
                        </p>
                      );
                    }
                    return (
                      <div className="space-y-2" data-testid="current-borrowers-list">
                        {borrowers.map((b) => (
                          <div
                            key={b.hold_id}
                            className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex justify-between items-start"
                            data-testid={`current-borrower-${b.hold_id}`}
                          >
                            <div>
                              <button
                                onClick={() => {
                                  closeBookModal();
                                  handleUserClick(b.user);
                                }}
                                className="font-semibold text-blue-600 hover:underline"
                              >
                                {b.user.name}
                              </button>
                              <p className="text-sm text-gray-600">{b.user.membership_number}</p>
                              <p className="text-sm text-gray-500">{b.user.email}</p>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                b.status === 'delivered'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {b.status === 'delivered' ? '📦 Delivered' : '📖 Borrowed'}
                              </span>
                              <p className="text-xs text-gray-500 mt-2">
                                Since: {b.borrowed_date ? new Date(b.borrowed_date).toLocaleDateString() : 'N/A'}
                              </p>
                              <p className="text-xs font-medium text-red-600 mt-1">
                                Due: {b.due_date ? new Date(b.due_date).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Pending Holds Queue */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FiClock className="w-5 h-5 text-amber-600" />
                    Holds Queue ({bookLibraryCard.holds_count})
                  </h3>
                  {bookLibraryCard.pending_holds.length > 0 ? (
                    <div className="space-y-2">
                      {bookLibraryCard.pending_holds.map((hold, index) => (
                        <div key={hold.hold_id} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </span>
                            <div>
                              <button
                                onClick={() => {
                                  closeBookModal();
                                  handleUserClick(hold.user);
                                }}
                                className="font-medium text-blue-600 hover:underline"
                              >
                                {hold.user.name}{hold.is_child_hold ? ` (${hold.child_name || 'Child'} — Children membership)` : ''}
                              </button>
                              <p className="text-xs text-gray-500">{hold.user.membership_number}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`px-2 py-1 rounded text-xs ${
                              hold.status === 'reserved' ? 'bg-green-100 text-green-700' :
                              hold.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {hold.status}
                            </span>
                            {hold.requested_pickup_date && (
                              <p className="text-xs text-gray-500 mt-1">
                                Pickup: {new Date(hold.requested_pickup_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                      No pending holds
                    </p>
                  )}
                </div>

                {/* Copy Slot Map — shows per-copy timeline (best-fit allocation) */}
                {bookLibraryCard.copy_map && bookLibraryCard.copy_map.length > 0 && (
                  <div className="mb-6" data-testid="copy-slot-map">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <FiBook className="w-5 h-5 text-amber-600" />
                      Copy Timeline ({bookLibraryCard.copy_map.length} {bookLibraryCard.copy_map.length === 1 ? 'copy' : 'copies'})
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Shows which physical copy serves each loan / reservation / pending hold,
                      assuming best-fit slot rotation. Useful for spotting overlaps and
                      verifying loan-extension feasibility.
                    </p>
                    <div className="grid gap-3" data-testid="copy-slot-grid">
                      {bookLibraryCard.copy_map.map((slot) => {
                        const isFree = slot.consumers.length === 0;
                        const lastEnd = isFree
                          ? null
                          : new Date(slot.consumers[slot.consumers.length - 1].end);
                        return (
                          <div
                            key={slot.copy_number}
                            className="border rounded-lg overflow-hidden"
                            data-testid={`copy-${slot.copy_number}`}
                          >
                            <div className="px-3 py-2 bg-amber-50 border-b text-sm font-semibold text-amber-900 flex justify-between">
                              <span>Copy {slot.copy_number}</span>
                              <span className="text-xs font-normal text-amber-700">
                                {isFree
                                  ? 'Available now'
                                  : `Next free: ${lastEnd ? new Date(lastEnd.getTime() + 86400000).toLocaleDateString() : '—'}`}
                              </span>
                            </div>
                            {isFree ? (
                              <p className="px-3 py-2 text-sm text-gray-500">No reservations scheduled.</p>
                            ) : (
                              <ol className="divide-y">
                                {slot.consumers.map((c, idx) => {
                                  const statusLabel =
                                    c.status === 'borrowed' ? 'Borrowed'
                                    : c.status === 'delivered' ? 'Delivered'
                                    : c.status === 'reserved' ? 'Reserved'
                                    : c.status === 'pending' ? 'Pending'
                                    : c.status === 'queued' ? 'Queued'
                                    : c.status;
                                  const badgeColor =
                                    c.status === 'borrowed' || c.status === 'delivered'
                                      ? 'bg-green-100 text-green-700'
                                      : c.status === 'reserved'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-amber-100 text-amber-700';
                                  return (
                                    <li
                                      key={c.hold_id}
                                      className="px-3 py-2 flex items-center justify-between gap-3"
                                      data-testid={`copy-${slot.copy_number}-row-${idx}`}
                                    >
                                      <div className="min-w-0 flex-1">
                                        <button
                                          onClick={() => {
                                            closeBookModal();
                                            handleUserClick(c.user);
                                          }}
                                          className="text-sm font-medium text-blue-600 hover:underline truncate block"
                                          disabled={!c.user}
                                        >
                                          {c.user?.name || 'Unknown member'}
                                          {c.user?.membership_number && (
                                            <span className="text-xs text-gray-500 ml-1">
                                              ({c.user.membership_number})
                                            </span>
                                          )}
                                        </button>
                                        {c.delayed && c.requested_start && (
                                          <p className="text-[11px] text-orange-600">
                                            requested {new Date(c.requested_start).toLocaleDateString()} — bumped to {new Date(c.start).toLocaleDateString()}
                                          </p>
                                        )}
                                      </div>
                                      <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${badgeColor}`}>
                                        {statusLabel}
                                      </span>
                                      <div className="text-xs text-right text-gray-600 whitespace-nowrap">
                                        <div>{new Date(c.start).toLocaleDateString()}</div>
                                        <div className="text-gray-400">↓</div>
                                        <div>{new Date(c.end).toLocaleDateString()}</div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Borrowing History */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FiList className="w-5 h-5 text-amber-600" />
                    Borrowing History
                  </h3>
                  {bookLibraryCard.borrowing_history.length > 0 ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="py-2 px-3 text-left font-medium text-gray-600">Member</th>
                            <th className="py-2 px-3 text-left font-medium text-gray-600">Borrowed</th>
                            <th className="py-2 px-3 text-left font-medium text-gray-600">Returned</th>
                            <th className="py-2 px-3 text-left font-medium text-gray-600">Type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {bookLibraryCard.borrowing_history.map((record, index) => (
                            <tr key={record.id || record.hold_id || `${record.book_id || ''}-${record.borrowed_date || index}`} className="hover:bg-gray-50">
                              <td className="py-2 px-3">
                                <button
                                  onClick={() => {
                                    closeBookModal();
                                    handleUserClick(record.user);
                                  }}
                                  className="text-blue-600 hover:underline"
                                >
                                  {record.user.name}
                                </button>
                              </td>
                              <td className="py-2 px-3 text-gray-600">
                                {record.borrowed_date ? new Date(record.borrowed_date).toLocaleDateString() : '—'}
                              </td>
                              <td className="py-2 px-3 text-gray-600">
                                {record.return_date ? new Date(record.return_date).toLocaleDateString() : '—'}
                              </td>
                              <td className="py-2 px-3">
                                <span className={`text-xs ${record.was_overdue ? 'text-red-600' : 'text-green-600'}`}>
                                  {record.was_overdue ? '⚠️ Overdue' : '✓ On time'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                      No borrowing history yet
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Update Modal */}
        {showSubscriptionModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  {subscriptionUpdate.status === 'active' ? '✅ Activate' : '⏸️ Deactivate'} Subscription
                </h3>
                <button
                  onClick={() => {
                    setShowSubscriptionModal(false);
                    setSubscriptionUpdate({ status: 'active', plan: 'cash', duration: 1, note: '' });
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-amber-50 rounded-lg">
                <p className="font-medium text-gray-800">{selectedUser.name}</p>
                <p className="text-sm text-gray-600">{selectedUser.email}</p>
              </div>

              {subscriptionUpdate.status === 'active' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
                    <select
                      value={subscriptionUpdate.plan}
                      onChange={(e) => setSubscriptionUpdate({...subscriptionUpdate, plan: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                    >
                      <option value="cash">Cash Payment</option>
                      <option value="monthly">Monthly (Online)</option>
                      <option value="annual">Annual (Online)</option>
                      <option value="complimentary">Complimentary/Free</option>
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Duration (months)</label>
                    <select
                      value={subscriptionUpdate.duration}
                      onChange={(e) => setSubscriptionUpdate({...subscriptionUpdate, duration: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                    >
                      <option value="1">1 month</option>
                      <option value="3">3 months</option>
                      <option value="6">6 months</option>
                      <option value="12">12 months (1 year)</option>
                    </select>
                  </div>
                </>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Note (optional)
                </label>
                <textarea
                  value={subscriptionUpdate.note}
                  onChange={(e) => setSubscriptionUpdate({...subscriptionUpdate, note: e.target.value})}
                  placeholder={subscriptionUpdate.status === 'active' 
                    ? "e.g., Paid ₹500 cash on Feb 7, 2026" 
                    : "e.g., Subscription expired, member requested cancellation"
                  }
                  className="w-full p-2 border border-gray-300 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200 h-20 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSubscriptionModal(false);
                    setSubscriptionUpdate({ status: 'active', plan: 'cash', duration: 1, note: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={updatingSubscription}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateSubscription}
                  disabled={updatingSubscription}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                    subscriptionUpdate.status === 'active'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  } disabled:opacity-50`}
                  data-testid="confirm-subscription-update"
                >
                  {updatingSubscription ? 'Updating...' : `Confirm ${subscriptionUpdate.status === 'active' ? 'Activation' : 'Deactivation'}`}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Fines Management Tab */}
        {activeTab === 'fines' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Fine Management</h2>
              <p className="text-sm text-gray-600">₹50 per day overdue (after 24h grace)</p>
            </div>

            {/* Summary Cards */}
            {finesSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="fines-accruing-card">
                  <p className="text-sm text-orange-600 font-medium">Accruing (books out)</p>
                  <p className="text-2xl font-bold text-orange-700">₹{finesSummary.total_accruing?.toFixed(0) || 0}</p>
                  <p className="text-xs text-orange-500">{finesSummary.accruing_count || 0} overdue books still out</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600 font-medium">Unpaid Fines</p>
                  <p className="text-2xl font-bold text-red-700">₹{finesSummary.total_unpaid?.toFixed(0) || 0}</p>
                  <p className="text-xs text-red-500">{finesSummary.unpaid_count || 0} fines • {finesSummary.users_with_outstanding_fines || 0} members</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">Paid Fines</p>
                  <p className="text-2xl font-bold text-green-700">₹{finesSummary.total_paid?.toFixed(0) || 0}</p>
                  <p className="text-xs text-green-500">{finesSummary.paid_count || 0} fines collected</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 font-medium">Waived Fines</p>
                  <p className="text-2xl font-bold text-gray-700">₹{finesSummary.total_waived?.toFixed(0) || 0}</p>
                  <p className="text-xs text-gray-500">{finesSummary.waived_count || 0} fines waived</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-600 font-medium">Total Fines</p>
                  <p className="text-2xl font-bold text-amber-700">₹{((finesSummary.total_unpaid || 0) + (finesSummary.total_paid || 0) + (finesSummary.total_waived || 0)).toFixed(0)}</p>
                  <p className="text-xs text-amber-500">{(finesSummary.unpaid_count || 0) + (finesSummary.paid_count || 0) + (finesSummary.waived_count || 0)} total records</p>
                </div>
              </div>
            )}

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { key: 'unpaid', label: 'Unpaid', color: 'red' },
                { key: 'paid', label: 'Paid', color: 'green' },
                { key: 'waived', label: 'Waived', color: 'gray' },
                { key: 'all', label: 'All', color: 'amber' }
              ].map(({ key, label, color }) => (
                <button
                  key={key}
                  onClick={() => { setFineFilter(key); setSelectedFineIds([]); }}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    fineFilter === key
                      ? `bg-${color}-600 text-white`
                      : `bg-${color}-50 text-${color}-700 hover:bg-${color}-100`
                  }`}
                  data-testid={`fine-filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Bulk action bar */}
            {selectedFineIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="fines-bulk-bar">
                <span className="text-sm font-semibold text-indigo-900">
                  {selectedFineIds.length} selected · ₹{getFilteredFines().filter(f => selectedFineIds.includes(f.id)).reduce((s, f) => s + (f.fee_amount || 0), 0).toFixed(0)}
                </span>
                <button
                  onClick={() => handleBulkFineAction('paid')}
                  disabled={bulkActing}
                  className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  data-testid="bulk-mark-paid-btn"
                >
                  {bulkActing ? 'Working…' : '✓ Mark as paid'}
                </button>
                <button
                  onClick={() => handleBulkFineAction('waive')}
                  disabled={bulkActing}
                  className="px-4 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
                  data-testid="bulk-waive-btn"
                >
                  {bulkActing ? 'Working…' : 'Waive'}
                </button>
                <button
                  onClick={() => setSelectedFineIds([])}
                  className="text-sm text-indigo-700 underline"
                  data-testid="bulk-clear-selection"
                >
                  Clear selection
                </button>
              </div>
            )}

            {/* Fines Table */}
            {loadingFines ? (
              <p className="text-center text-gray-600 py-8">Loading fines...</p>
            ) : getFilteredFines().length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FiDollarSign className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No fines found</p>
                <p className="text-sm">No {fineFilter !== 'all' ? fineFilter : ''} fines to display</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          data-testid="select-all-fines"
                          checked={getFilteredFines().filter(f => !f.paid && !f.waived).length > 0 && getFilteredFines().filter(f => !f.paid && !f.waived).every(f => selectedFineIds.includes(f.id))}
                          onChange={(e) => {
                            const selectable = getFilteredFines().filter(f => !f.paid && !f.waived).map(f => f.id);
                            setSelectedFineIds(e.target.checked ? selectable : []);
                          }}
                          className="w-4 h-4"
                        />
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Member</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Book</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Days Overdue</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getFilteredFines().map((fine) => (
                      <tr key={fine.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3">
                          {!fine.paid && !fine.waived && (
                            <input
                              type="checkbox"
                              data-testid={`select-fine-${fine.id}`}
                              checked={selectedFineIds.includes(fine.id)}
                              onChange={(e) => setSelectedFineIds(e.target.checked ? [...selectedFineIds, fine.id] : selectedFineIds.filter(id => id !== fine.id))}
                              className="w-4 h-4"
                            />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-800">{fine.user_name}</p>
                            <p className="text-xs text-gray-500">{fine.membership_number}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-800">{fine.book_title}</p>
                          {fine.book_id === 'manual' && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Manual</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {fine.days_overdue > 0 ? (
                            <span className="text-red-600 font-medium">{fine.days_overdue} days</span>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {adjustingFine === fine.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={adjustAmount}
                                onChange={(e) => setAdjustAmount(e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="₹"
                              />
                              <button
                                onClick={() => handleAdjustFine(fine.id)}
                                className="px-2 py-1 bg-green-500 text-white rounded text-xs"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => { setAdjustingFine(null); setAdjustAmount(''); }}
                                className="px-2 py-1 bg-gray-400 text-white rounded text-xs"
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <span className="font-semibold text-gray-800">₹{fine.fee_amount}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {fine.paid ? (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Paid</span>
                          ) : fine.waived ? (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">Waived</span>
                          ) : fine.accruing ? (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium" title="Book still out — fine grows daily and becomes a record on return">Accruing</span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Unpaid</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {fine.accruing ? (
                            <p className="text-xs text-gray-500 italic">Book still out — fine grows ₹50/day and is recorded on return</p>
                          ) : !fine.paid && !fine.waived && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleWaiveFine(fine.id, fine.user_name)}
                                className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs transition-colors"
                                data-testid={`waive-fine-${fine.id}`}
                              >
                                Waive
                              </button>
                              <button
                                onClick={() => { setAdjustingFine(fine.id); setAdjustAmount(fine.fee_amount.toString()); }}
                                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs transition-colors"
                                data-testid={`adjust-fine-${fine.id}`}
                              >
                                Adjust
                              </button>
                              <button
                                onClick={() => handleClearUserFines(fine.user_id, fine.user_name)}
                                className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs transition-colors"
                                data-testid={`clear-all-fines-${fine.id}`}
                                title="Clear all fines for this user"
                              >
                                Clear All
                              </button>
                            </div>
                          )}
                          {fine.notes && (
                            <p className="text-xs text-gray-500 mt-1 italic">{fine.notes}</p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {/* Hold Rejections Audit Tab */}
        {activeTab === 'rejections' && (
          <div className="space-y-6" data-testid="rejections-tab-content">

            {/* Stuck Rental Orders — global view */}
            <div className="bg-white rounded-xl shadow-lg p-8" data-testid="stuck-rentals-section">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <FiAlertCircle className="w-6 h-6 text-red-600" />
                    Stuck Rental Orders
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Abandoned Razorpay checkouts (status: <code className="bg-gray-100 px-1 rounded">pending_payment</code>). Block new rentals on the same book for the same member until cleared.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchStuckRentals}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold"
                    data-testid="stuck-rentals-refresh-btn"
                  >Refresh</button>
                  {stuckRentals.total > 0 && (
                    <button
                      onClick={handleClearAllStuckRentals}
                      disabled={clearingStuckId === 'ALL'}
                      className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                      data-testid="stuck-rentals-clear-all-btn"
                    >{clearingStuckId === 'ALL' ? 'Clearing…' : `Clear all (${stuckRentals.total})`}</button>
                  )}
                </div>
              </div>

              {loadingStuckRentals ? (
                <p className="text-center text-gray-500 py-6">Loading…</p>
              ) : stuckRentals.total === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FiCheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                  <p className="font-semibold text-gray-700">No stuck rental orders.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-50 border-b border-red-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold text-red-900">Member</th>
                        <th className="text-left px-4 py-2 font-semibold text-red-900">Book</th>
                        <th className="text-right px-4 py-2 font-semibold text-red-900">Amount</th>
                        <th className="text-left px-4 py-2 font-semibold text-red-900">Order ID</th>
                        <th className="text-left px-4 py-2 font-semibold text-red-900">Abandoned</th>
                        <th className="text-right px-4 py-2 font-semibold text-red-900">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stuckRentals.rentals.map((r) => (
                        <tr key={r.id} className="border-b border-gray-100 hover:bg-red-50/30" data-testid={`stuck-rental-row-${r.id}`}>
                          <td className="px-4 py-2">
                            <p className="font-medium text-gray-800">{r.user?.name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">{r.user?.email}</p>
                          </td>
                          <td className="px-4 py-2 text-gray-800">{r.book?.title || 'Unknown'}</td>
                          <td className="px-4 py-2 text-right font-semibold text-gray-800">₹{r.price ?? '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-500 font-mono">{r.razorpay_order_id || '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">
                            {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              onClick={() => handleClearStuckRental(r.id, r.book?.title || 'this book', r.user?.name || 'this member')}
                              disabled={clearingStuckId === r.id}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                              data-testid={`clear-stuck-rental-${r.id}`}
                            >{clearingStuckId === r.id ? '…' : 'Clear'}</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Existing Hold Rejection Audit content */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Hold Rejection Audit</h2>
                  <p className="text-sm text-gray-600 mt-1">Members who tried to place a hold but were blocked — see which books cause the most friction.</p>
                </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Window:</label>
                <select
                  value={rejectionWindowDays}
                  onChange={(e) => {
                    const d = parseInt(e.target.value, 10);
                    setRejectionWindowDays(d);
                    fetchHoldRejections(d);
                  }}
                  className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-amber-500 focus:outline-none"
                  data-testid="rejections-window-select"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={60}>Last 60 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
                <button
                  onClick={() => fetchHoldRejections(rejectionWindowDays)}
                  className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold"
                  data-testid="rejections-refresh-btn"
                >
                  Refresh
                </button>
                <a
                  href={`${API}/admin/hold-rejections.csv?days=${rejectionWindowDays}`}
                  onClick={(e) => {
                    // For authenticated download, fetch as blob with auth header
                    e.preventDefault();
                    (async () => {
                      try {
                        const res = await axios.get(
                          `${API}/admin/hold-rejections.csv?days=${rejectionWindowDays}`,
                          { headers: getAuthHeader(), responseType: 'blob' }
                        );
                        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `hold_rejections_last_${rejectionWindowDays}d.csv`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (err) {
                        alert('Failed to download CSV');
                      }
                    })();
                  }}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold cursor-pointer"
                  data-testid="rejections-csv-btn"
                >
                  Export CSV
                </a>
              </div>
            </div>

            {loadingRejections ? (
              <p className="text-center text-gray-500 py-8">Loading…</p>
            ) : !holdRejections || holdRejections.total_rejections === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FiCheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="font-semibold text-gray-700">No rejections in this window.</p>
                <p className="text-sm mt-1">Members aren't getting blocked by the queue logic — good sign.</p>
              </div>
            ) : (
              <>
                {/* Per-book frequency leaderboard */}
                {holdRejections.by_book && holdRejections.by_book.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Books with most rejections</h3>
                    <p className="text-xs text-gray-500 mb-3">Books frequently rejecting members are strong signals to order more copies.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-50 border-b border-amber-200">
                          <tr>
                            <th className="text-left px-4 py-2 font-semibold text-amber-900">Book</th>
                            <th className="text-right px-4 py-2 font-semibold text-amber-900"># Rejections</th>
                            <th className="text-left px-4 py-2 font-semibold text-amber-900">Last attempted</th>
                          </tr>
                        </thead>
                        <tbody>
                          {holdRejections.by_book.slice(0, 15).map((b, idx) => (
                            <tr key={(b.book_id || 'unknown') + '_' + idx} className="border-b border-gray-100 hover:bg-amber-50/30">
                              <td className="px-4 py-2" data-testid={`rejection-book-title-${idx}`}>
                                {b.book_id ? (
                                  <button
                                    onClick={() => navigate(`/book/${b.book_id}`)}
                                    className="text-amber-700 hover:text-amber-900 hover:underline font-medium text-left"
                                    data-testid={`rejection-book-link-${idx}`}
                                  >
                                    {b.book_title}
                                  </button>
                                ) : (
                                  <span className="text-gray-800">{b.book_title}</span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-right">
                                <span className="inline-block px-2 py-0.5 bg-red-100 text-red-700 rounded font-semibold">{b.count}</span>
                              </td>
                              <td className="px-4 py-2 text-gray-600">
                                {b.last_attempted ? new Date(b.last_attempted).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent rejections list */}
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">Recent rejections ({holdRejections.total_rejections})</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">When</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Member</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Book</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Requested</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Earliest offered</th>
                          <th className="text-left px-4 py-2 font-semibold text-gray-700">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {holdRejections.rejections.map((r, idx) => (
                          <tr key={(r.user_id || 'u') + '_' + (r.rejected_at || idx)} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                              {r.rejected_at ? new Date(r.rejected_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-800" data-testid={`rejection-user-${idx}`}>{r.user_email || '—'}</td>
                            <td className="px-4 py-2">
                              {r.book_id ? (
                                <button
                                  onClick={() => navigate(`/book/${r.book_id}`)}
                                  className="text-amber-700 hover:text-amber-900 hover:underline text-left"
                                >
                                  {r.book_title || '—'}
                                </button>
                              ) : (
                                <span className="text-gray-800">{r.book_title || '—'}</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-600">{r.requested_date || '—'}</td>
                            <td className="px-4 py-2 text-gray-600">{r.earliest_offered || '—'}</td>
                            <td className="px-4 py-2">
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                                {(r.reason || 'unknown').replace(/_/g, ' ')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
        )}

        {/* Reviews Moderation Tab */}
        {activeTab === 'reviews' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Review Moderation</h2>
              <p className="text-sm text-gray-600">
                {pendingReviews.length} review{pendingReviews.length !== 1 ? 's' : ''} pending approval
              </p>
            </div>

            {loading ? (
              <p className="text-center text-gray-600 py-8">Loading reviews...</p>
            ) : pendingReviews.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FiMessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No pending reviews</p>
                <p className="text-sm">New reviews will appear here for approval</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingReviews.map((review) => (
                  <div key={review.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-800 text-lg">{review.book_title}</h3>
                        <p className="text-sm text-gray-600">by {review.user_name || 'Anonymous'}</p>
                      </div>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <FiStar
                            key={`adm-rstar-${review.id}-${i}`}
                            className={`w-5 h-5 ${i < review.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </div>
                    
                    {review.review_text && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-gray-700 whitespace-pre-wrap">{review.review_text}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        Submitted: {new Date(review.created_at).toLocaleDateString()} at {new Date(review.created_at).toLocaleTimeString()}
                      </span>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleRejectReview(review.id)}
                          disabled={processingReview === review.id}
                          className="flex items-center space-x-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                          data-testid={`reject-review-${review.id}`}
                        >
                          <FiXCircle className="w-4 h-4" />
                          <span>Reject</span>
                        </button>
                        <button
                          onClick={() => handleApproveReview(review.id)}
                          disabled={processingReview === review.id}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          data-testid={`approve-review-${review.id}`}
                        >
                          <FiCheckCircle className="w-4 h-4" />
                          <span>Approve</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Add-ons Tab */}
        {activeTab === 'addons' && (
          <div className="space-y-6">
            <SundayClosuresCard getAuthHeader={getAuthHeader} />
            {/* Header */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Membership Add-Ons</h2>
                  <p className="text-sm text-gray-600">Create and manage subscription add-ons like Book Clubs, Priority Access, etc.</p>
                </div>
                <button
                  onClick={() => { setEditingAddonId(null); _resetNewAddon(); setShowCreateAddon(true); }}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
                  data-testid="create-addon-btn"
                >
                  <FiPlus className="w-4 h-4" />
                  Create Add-On
                </button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{addons.length}</p>
                  <p className="text-sm text-blue-700">Total Add-Ons</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{addons.filter(a => a.is_active).length}</p>
                  <p className="text-sm text-green-700">Active</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{addons.filter(a => a.visible_to_members).length}</p>
                  <p className="text-sm text-purple-700">Visible to Members</p>
                </div>
              </div>
            </div>

            {/* Add-ons List */}
            {loadingAddons ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <p className="text-gray-600">Loading add-ons...</p>
              </div>
            ) : addons.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <FiPackage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No add-ons created yet</p>
                <button
                  onClick={() => setShowCreateAddon(true)}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                >
                  Create Your First Add-On
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {addons.map(addon => (
                  <div key={addon.id} className="bg-white rounded-xl shadow-lg p-6">
                    {addon.image_url && (
                      <img src={addon.image_url} alt={addon.name} className="w-full h-40 object-cover rounded-lg mb-3" />
                    )}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-gray-800">{addon.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          addon.addon_type === 'book_club' ? 'bg-purple-100 text-purple-700' :
                          addon.addon_type === 'priority_access' ? 'bg-blue-100 text-blue-700' :
                          addon.addon_type === 'extra_books' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {addon.addon_type?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-amber-600">₹{addon.base_price}</p>
                        <p className="text-xs text-gray-500">
                          {addon.payment_mode === 'autopay' ? `autopay / ${addon.billing_frequency}` : 'one-time'}
                        </p>
                        {addon.allow_non_members && addon.non_member_price != null && addon.non_member_price !== addon.base_price && (
                          <p className="text-xs text-purple-700 mt-1">
                            Non-members: <span className="font-semibold">₹{addon.non_member_price}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {(addon.event_datetime || addon.location) && (
                      <div className="mb-3 space-y-1 text-xs text-gray-700">
                        {addon.event_datetime && (
                          <p>📅 <span className="font-medium">{new Date(addon.event_datetime).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}{addon.event_end_datetime ? ` – ${new Date(addon.event_end_datetime).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}` : ''}</span></p>
                        )}
                        {addon.location && (
                          <p>📍 <span className="font-medium">{addon.location}</span></p>
                        )}
                      </div>
                    )}

                    {addon.external_registration_url && (
                      <div className="mb-3" data-testid={`addon-external-clicks-${addon.id}`}>
                        <p className="text-xs text-blue-700">
                          🔗 Partner registration link · <span className="font-semibold">{addon.external_link_clicks || 0}</span> click{(addon.external_link_clicks || 0) === 1 ? '' : 's'} out
                        </p>
                        {addon.external_clicks_by_day && Object.keys(addon.external_clicks_by_day).length > 0 && (
                          <p className="text-[11px] text-gray-500 mt-0.5" data-testid={`addon-external-clicks-daily-${addon.id}`}>
                            {Object.entries(addon.external_clicks_by_day)
                              .sort((a, b) => b[0].localeCompare(a[0]))
                              .slice(0, 7)
                              .map(([d, c]) => `${new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}: ${c}`)
                              .join(' · ')}
                          </p>
                        )}
                      </div>
                    )}

                    {Array.isArray(addon.ticket_categories) && addon.ticket_categories.length > 0 && (
                      <div className="mb-3 text-xs text-gray-700 space-y-0.5" data-testid={`addon-categories-${addon.id}`}>
                        {addon.ticket_categories.map((c) => (
                          <p key={c.id}>
                            🎟 <span className="font-medium">{c.name}</span> — ₹{c.member_price}
                            {c.non_member_price != null ? ` / ₹${c.non_member_price} non-member` : ''}
                            {c.capacity != null ? ` · ${c.sold || 0}/${c.capacity} sold` : ((c.sold || 0) > 0 ? ` · ${c.sold} sold` : '')}
                            {c.deadline ? ` · till ${new Date(c.deadline).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}
                          </p>
                        ))}
                      </div>
                    )}

                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{addon.description}</p>
                    
                    {addon.features && addon.features.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs text-gray-500 mb-1">Features:</p>
                        <div className="flex flex-wrap gap-1">
                          {addon.features.slice(0, 3).map((feature, idx) => (
                            <span key={`${addon.id}-feat-${idx}-${String(feature).slice(0, 16)}`} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{feature}</span>
                          ))}
                          {addon.features.length > 3 && (
                            <span className="text-xs text-gray-500">+{addon.features.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${addon.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {addon.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${addon.visible_to_members ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                        {addon.visible_to_members ? '👁️ Visible' : '🔒 Hidden'}
                      </span>
                      {addon.max_capacity ? (
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          (addon.current_subscribers || 0) >= addon.max_capacity
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {addon.current_subscribers || 0} / {addon.max_capacity} sold
                        </span>
                      ) : addon.current_subscribers > 0 ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                          {addon.current_subscribers} subscribers
                        </span>
                      ) : null}
                    </div>

                    <div className="flex gap-2 pt-3 border-t flex-wrap">
                      <button
                        onClick={() => openRsvpModal(addon)}
                        className="flex-1 min-w-[140px] px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 font-medium"
                        data-testid={`view-rsvps-${addon.id}`}
                      >
                        View RSVPs ({addon.current_subscribers || 0})
                      </button>
                      <button
                        onClick={() => openEditAddon(addon)}
                        className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium"
                        data-testid={`edit-addon-${addon.id}`}
                      >
                        ✎ Edit
                      </button>
                      <button
                        onClick={() => handleUpdateAddon(addon.id, { visible_to_members: !addon.visible_to_members })}
                        className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        {addon.visible_to_members ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={() => handleUpdateAddon(addon.id, { is_active: !addon.is_active })}
                        className={`px-3 py-1.5 text-sm rounded ${addon.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                      >
                        {addon.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteAddon(addon.id)}
                        className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Create Add-On Modal */}
            {showCreateAddon && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">{editingAddonId ? 'Edit Add-On' : 'Create New Add-On'}</h3>
                    <button onClick={() => { setShowCreateAddon(false); setEditingAddonId(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Templates */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Quick Start with Template:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(addonTemplates).map(key => (
                        <button
                          key={key}
                          onClick={() => applyTemplate(key)}
                          className="px-3 py-1.5 text-sm bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100"
                        >
                          {key.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={newAddon.name}
                        onChange={(e) => setNewAddon({...newAddon, name: e.target.value})}
                        className="w-full p-2 border rounded-lg focus:border-amber-500"
                        placeholder="e.g., Saturday Book Reading - June"
                        data-testid="addon-name-input"
                      />
                    </div>

                    {/* Picture upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Picture</label>
                      <div className="flex items-start gap-3">
                        {newAddon.image_url ? (
                          <div className="relative">
                            <img src={newAddon.image_url} alt="addon" className="w-24 h-24 object-cover rounded-lg border" />
                            <button
                              onClick={() => setNewAddon({...newAddon, image_url: ''})}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                              title="Remove image"
                            >
                              <FiX className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                            No image
                          </div>
                        )}
                        <div className="flex-1">
                          <label className="inline-block px-3 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 cursor-pointer text-sm font-medium border border-amber-200" data-testid="addon-image-upload">
                            {uploadingAddonImage ? 'Uploading...' : (newAddon.image_url ? 'Replace image' : 'Upload image')}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              disabled={uploadingAddonImage}
                              onChange={(e) => handleUploadAddonImage(e.target.files?.[0])}
                            />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">JPG, PNG, or WEBP. Max 5MB.</p>
                        </div>
                      </div>
                    </div>

                    {/* Time and Date */}
                    <div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Starts</label>
                          <input
                            type="datetime-local"
                            value={newAddon.event_datetime || ''}
                            onChange={(e) => setNewAddon({...newAddon, event_datetime: e.target.value})}
                            className="w-full p-2 border rounded-lg focus:border-amber-500"
                            data-testid="addon-datetime-input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ends</label>
                          <input
                            type="datetime-local"
                            value={newAddon.event_end_datetime || ''}
                            onChange={(e) => setNewAddon({...newAddon, event_end_datetime: e.target.value})}
                            className="w-full p-2 border rounded-lg focus:border-amber-500"
                            data-testid="addon-end-datetime-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Leave blank for ongoing services (e.g., monthly book club).</p>
                    </div>

                    {/* Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={newAddon.location || ''}
                        onChange={(e) => setNewAddon({...newAddon, location: e.target.value})}
                        className="w-full p-2 border rounded-lg focus:border-amber-500"
                        placeholder="e.g., The Hyd Library, 2nd Floor, Banjara Hills"
                        data-testid="addon-location-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={newAddon.description}
                        onChange={(e) => setNewAddon({...newAddon, description: e.target.value})}
                        className="w-full p-2 border rounded-lg focus:border-amber-500 h-20"
                        placeholder="What members get with this add-on"
                        data-testid="addon-description-input"
                      />
                    </div>

                    {/* Payment Mode + Price + Billing */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                        <select
                          value={newAddon.payment_mode}
                          onChange={(e) => {
                            const mode = e.target.value;
                            setNewAddon({
                              ...newAddon,
                              payment_mode: mode,
                              billing_frequency: mode === 'one_time' ? 'one-time' : (newAddon.billing_frequency === 'one-time' ? 'monthly' : newAddon.billing_frequency)
                            });
                          }}
                          className="w-full p-2 border rounded-lg focus:border-amber-500"
                          data-testid="addon-payment-mode-select"
                        >
                          <option value="one_time">One-time payment</option>
                          <option value="autopay">Autopay (recurring)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Member Price (₹)</label>
                        <input
                          type="number"
                          min="0"
                          value={newAddon.base_price}
                          onChange={(e) => setNewAddon({...newAddon, base_price: parseFloat(e.target.value) || 0})}
                          className="w-full p-2 border rounded-lg focus:border-amber-500"
                          data-testid="addon-price-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">What active library members pay (0 = free for members).</p>
                      </div>
                    </div>

                    {/* Dual pricing: allow non-members */}
                    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3">
                      <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newAddon.allow_non_members}
                          onChange={(e) => setNewAddon({
                            ...newAddon,
                            allow_non_members: e.target.checked,
                            non_member_price: e.target.checked ? (newAddon.non_member_price ?? newAddon.base_price) : null
                          })}
                          className="mt-1"
                          data-testid="addon-allow-non-members-checkbox"
                        />
                        <span>
                          <span className="font-semibold">Allow non-members to RSVP</span>
                          <span className="block text-xs text-gray-600 mt-0.5">
                            When ON, anyone with an account can attend even without an active subscription. Charge them a separate price below.
                          </span>
                        </span>
                      </label>

                      {newAddon.allow_non_members && (
                        <div className="mt-3 ml-6">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Non-member Price (₹)</label>
                          <input
                            type="number"
                            min="0"
                            value={newAddon.non_member_price ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setNewAddon({
                                ...newAddon,
                                non_member_price: v === '' ? null : Math.max(0, parseFloat(v) || 0)
                              });
                            }}
                            className="w-full p-2 border rounded-lg focus:border-amber-500"
                            placeholder={`e.g., ${Math.max(newAddon.base_price * 2, newAddon.base_price + 100, 200)} (typically higher than member price)`}
                            data-testid="addon-non-member-price-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">What non-members (no active subscription) pay. 0 = free for them too.</p>

                          <div className="mt-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Attendance for non-members</label>
                            <select
                              value={newAddon.attendance_policy}
                              onChange={(e) => setNewAddon({...newAddon, attendance_policy: e.target.value})}
                              className="w-full p-2 border rounded-lg focus:border-amber-500"
                              data-testid="addon-attendance-policy-select"
                            >
                              <option value="reservation">📅 Reservation required (pre-book online)</option>
                              <option value="walk_ins">🚶 Walk-ins welcome (drop in, no pre-RSVP)</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Shown as a coloured chip on the member home page.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Free RSVP option — coexists with paid tickets & walk-ins */}
                    <label className="flex items-start gap-2 border border-emerald-200 bg-emerald-50/50 rounded-lg p-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!newAddon.allow_free_rsvp}
                        onChange={(e) => setNewAddon({...newAddon, allow_free_rsvp: e.target.checked})}
                        className="mt-0.5"
                        data-testid="addon-allow-free-rsvp"
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-700">Also allow a free RSVP (no payment)</span>
                        <span className="block text-xs text-gray-600 mt-0.5">
                          Adds an &quot;RSVP free — just attending&quot; button alongside any paid tickets, for people (members included) who just want a headcount spot. They&apos;ll appear in the RSVP list tagged &quot;Free RSVP&quot;.
                        </span>
                      </span>
                    </label>

                    {/* External registration (partner-hosted) */}
                    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">External registration link <span className="font-normal text-gray-500">(partner-hosted event)</span></label>
                      <input
                        type="url"
                        value={newAddon.external_registration_url || ''}
                        onChange={(e) => setNewAddon({...newAddon, external_registration_url: e.target.value})}
                        className="w-full p-2 border rounded-lg focus:border-blue-500"
                        placeholder="https://partner-site.com/register"
                        data-testid="addon-external-url-input"
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        If someone else is hosting this event and we&apos;re just providing the space, paste their registration page here.
                        Visitors will see a &quot;Register on host&apos;s site&quot; button instead of paying through us — and we count every click-out.
                      </p>
                    </div>

                    {/* Ticket categories */}
                    {newAddon.payment_mode === 'one_time' && (
                      <div className="border border-purple-200 bg-purple-50/50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-sm font-medium text-gray-700">Ticket categories <span className="font-normal text-gray-500">(optional — e.g. Early bird)</span></label>
                          <button
                            type="button"
                            onClick={() => setNewAddon({ ...newAddon, ticket_categories: [...(newAddon.ticket_categories || []), { id: (window.crypto?.randomUUID?.() || `cat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`), name: '', member_price: 0, non_member_price: null, capacity: '', deadline: '', sold: 0 }] })}
                            className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded hover:bg-purple-200 font-medium"
                            data-testid="addon-add-category"
                          >
                            + Add category
                          </button>
                        </div>
                        <p className="text-xs text-gray-600 mb-2">
                          Each category has its own member &amp; non-member price, an optional ticket cap, and an optional sales deadline.
                          When categories exist, attendees pick one instead of the single price above. Non-member price only applies when non-member RSVPs are allowed.
                        </p>
                        {(newAddon.ticket_categories || []).map((c, idx) => (
                          <div key={c.id || idx} className="bg-white border rounded p-2 mb-2" data-testid={`addon-category-row-${idx}`}>
                            <div className="flex items-center justify-between mb-1.5 gap-2">
                              <input
                                type="text"
                                placeholder="Category name (e.g. Early bird) *"
                                value={c.name}
                                onChange={(e) => setNewAddon({ ...newAddon, ticket_categories: newAddon.ticket_categories.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) })}
                                className="flex-1 p-1.5 border rounded text-sm"
                                data-testid={`addon-category-name-${idx}`}
                              />
                              <button
                                type="button"
                                onClick={() => setNewAddon({ ...newAddon, ticket_categories: newAddon.ticket_categories.filter((_, i) => i !== idx) })}
                                className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                                data-testid={`addon-category-remove-${idx}`}
                              >
                                Remove
                              </button>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                              <div>
                                <label className="block text-[10px] text-gray-500">Member ₹</label>
                                <input
                                  type="number" min="0"
                                  value={c.member_price ?? ''}
                                  onChange={(e) => setNewAddon({ ...newAddon, ticket_categories: newAddon.ticket_categories.map((x, i) => i === idx ? { ...x, member_price: e.target.value === '' ? 0 : Math.max(0, parseFloat(e.target.value) || 0) } : x) })}
                                  className="w-full p-1.5 border rounded text-xs"
                                  data-testid={`addon-category-member-price-${idx}`}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500">Non-member ₹</label>
                                <input
                                  type="number" min="0" placeholder="—"
                                  value={c.non_member_price ?? ''}
                                  onChange={(e) => setNewAddon({ ...newAddon, ticket_categories: newAddon.ticket_categories.map((x, i) => i === idx ? { ...x, non_member_price: e.target.value === '' ? null : Math.max(0, parseFloat(e.target.value) || 0) } : x) })}
                                  className="w-full p-1.5 border rounded text-xs"
                                  data-testid={`addon-category-non-member-price-${idx}`}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500">Ticket cap</label>
                                <input
                                  type="number" min="1" placeholder="∞"
                                  value={c.capacity ?? ''}
                                  onChange={(e) => setNewAddon({ ...newAddon, ticket_categories: newAddon.ticket_categories.map((x, i) => i === idx ? { ...x, capacity: e.target.value } : x) })}
                                  className="w-full p-1.5 border rounded text-xs"
                                  data-testid={`addon-category-capacity-${idx}`}
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] text-gray-500">On sale till</label>
                                <input
                                  type="datetime-local"
                                  value={c.deadline || ''}
                                  onChange={(e) => setNewAddon({ ...newAddon, ticket_categories: newAddon.ticket_categories.map((x, i) => i === idx ? { ...x, deadline: e.target.value } : x) })}
                                  className="w-full p-1.5 border rounded text-xs"
                                  data-testid={`addon-category-deadline-${idx}`}
                                />
                              </div>
                            </div>
                            {(c.sold || 0) > 0 && <p className="text-[10px] text-gray-500 mt-1">{c.sold} already sold</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {newAddon.payment_mode === 'autopay' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Billing Frequency</label>
                        <select
                          value={newAddon.billing_frequency === 'one-time' ? 'monthly' : newAddon.billing_frequency}
                          onChange={(e) => setNewAddon({...newAddon, billing_frequency: e.target.value})}
                          className="w-full p-2 border rounded-lg focus:border-amber-500"
                        >
                          <option value="monthly">Every month</option>
                          <option value="annual">Every year</option>
                        </select>
                      </div>
                    )}

                    {/* Maximum purchases / capacity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Purchases</label>
                      <input
                        type="number"
                        min="0"
                        value={newAddon.max_capacity ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setNewAddon({
                            ...newAddon,
                            max_capacity: v === '' ? null : Math.max(0, parseInt(v, 10) || 0)
                          });
                        }}
                        className="w-full p-2 border rounded-lg focus:border-amber-500"
                        placeholder="Leave blank for unlimited"
                        data-testid="addon-max-capacity-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Cap the total number of members who can buy this add-on. Once full, the Subscribe button becomes disabled.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={newAddon.addon_type}
                        onChange={(e) => setNewAddon({...newAddon, addon_type: e.target.value})}
                        className="w-full p-2 border rounded-lg focus:border-amber-500"
                      >
                        <option value="workshop">Workshop / Event</option>
                        <option value="book_club">Book Club</option>
                        <option value="priority_access">Priority Access</option>
                        <option value="extra_books">Extra Books</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Features (optional bullet points)</label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={newFeature}
                          onChange={(e) => setNewFeature(e.target.value)}
                          className="flex-1 p-2 border rounded-lg focus:border-amber-500"
                          placeholder="Add a feature"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newFeature.trim()) {
                              setNewAddon({...newAddon, features: [...newAddon.features, newFeature.trim()]});
                              setNewFeature('');
                            }
                          }}
                        />
                        <button
                          onClick={() => {
                            if (newFeature.trim()) {
                              setNewAddon({...newAddon, features: [...newAddon.features, newFeature.trim()]});
                              setNewFeature('');
                            }
                          }}
                          className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                          Add
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {newAddon.features.map((feature, idx) => (
                          <span key={`new-feat-${idx}-${String(feature).slice(0, 16)}`} className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded flex items-center gap-1">
                            {feature}
                            <button onClick={() => setNewAddon({...newAddon, features: newAddon.features.filter((_, i) => i !== idx)})}>
                              <FiX className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={newAddon.visible_to_members}
                        onChange={(e) => setNewAddon({...newAddon, visible_to_members: e.target.checked})}
                        data-testid="addon-visible-checkbox"
                      />
                      Make visible to members immediately
                    </label>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => { setShowCreateAddon(false); setEditingAddonId(null); }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAddon}
                      disabled={!newAddon.name || !newAddon.description}
                      className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                      data-testid="save-addon-btn"
                    >
                      {editingAddonId ? 'Save Changes' : 'Create Add-On'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* RSVP Roster Modal */}
            {rsvpAddon && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="rsvp-modal">
                <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">RSVPs — {rsvpAddon.name}</h3>
                      {rsvpData?.addon?.event_datetime && (
                        <p className="text-sm text-gray-600 mt-1">
                          📅 {new Date(rsvpData.addon.event_datetime).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                          {rsvpData.addon.location && <> · 📍 {rsvpData.addon.location}</>}
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mt-1">
                        <span className="font-semibold text-gray-700">{rsvpData?.total ?? 0}</span> signed up
                        {rsvpData?.addon?.max_capacity ? <> · {rsvpData.addon.max_capacity - (rsvpData.total ?? 0)} spots left</> : null}
                      </p>
                    </div>
                    <button onClick={() => { setRsvpAddon(null); setRsvpData(null); _resetCashSubForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>

                  {loadingRsvp ? (
                    <p className="text-center text-gray-500 py-8">Loading roster...</p>
                  ) : (
                    <>
                      {/* Add cash subscriber - toggle button */}
                      <div className="mb-4">
                        {!showAddCashSubscriber ? (
                          <button
                            onClick={() => setShowAddCashSubscriber(true)}
                            className="w-full py-2 border-2 border-dashed border-amber-300 text-amber-700 font-medium rounded-lg hover:bg-amber-50 transition-colors"
                            data-testid="add-cash-subscriber-btn"
                          >
                            + Add subscriber (cash / offline payment)
                          </button>
                        ) : (
                          <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-semibold text-gray-800">Add Cash / Offline Subscriber</h4>
                              <button onClick={_resetCashSubForm} className="text-gray-500 hover:text-gray-700">
                                <FiX className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Mode toggle */}
                            <div className="flex gap-2 mb-3">
                              <button
                                onClick={() => { setCashSubMode('existing'); setCashSubSelectedUser(null); }}
                                className={`flex-1 py-1.5 text-sm font-medium rounded ${cashSubMode === 'existing' ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 border'}`}
                                data-testid="cash-sub-mode-existing"
                              >
                                Existing member
                              </button>
                              <button
                                onClick={() => { setCashSubMode('walkin'); setCashSubUserQuery(''); setCashSubUserResults([]); setCashSubSelectedUser(null); }}
                                className={`flex-1 py-1.5 text-sm font-medium rounded ${cashSubMode === 'walkin' ? 'bg-amber-600 text-white' : 'bg-white text-gray-700 border'}`}
                                data-testid="cash-sub-mode-walkin"
                              >
                                Walk-in (new person)
                              </button>
                            </div>

                            {cashSubMode === 'existing' ? (
                              <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Search by name, email, phone, or membership #</label>
                                <input
                                  type="text"
                                  value={cashSubUserQuery}
                                  onChange={(e) => searchUsers(e.target.value)}
                                  className="w-full p-2 border rounded text-sm"
                                  placeholder="Type at least 2 characters..."
                                  data-testid="cash-sub-user-search"
                                />
                                {cashSubUserResults.length > 0 && !cashSubSelectedUser && (
                                  <div className="mt-1 bg-white border rounded shadow-sm max-h-40 overflow-y-auto">
                                    {cashSubUserResults.map((u) => (
                                      <button
                                        key={u.id}
                                        onClick={() => { setCashSubSelectedUser(u); setCashSubUserQuery(`${u.name} (${u.email})`); setCashSubUserResults([]); }}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 border-b last:border-0"
                                      >
                                        <div className="flex justify-between">
                                          <span className="font-medium text-gray-800">{u.name}</span>
                                          <span className={`text-xs px-1.5 py-0.5 rounded ${u.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                            {u.subscription_status === 'active' ? 'Member' : 'Non-member'}
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-500">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {cashSubSelectedUser && (
                                  <div className="mt-2 p-2 bg-white border border-amber-300 rounded text-sm">
                                    Selected: <span className="font-semibold">{cashSubSelectedUser.name}</span>{' '}
                                    <button onClick={() => { setCashSubSelectedUser(null); setCashSubUserQuery(''); }} className="text-xs text-blue-600 hover:underline ml-2">change</button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                <input
                                  type="text"
                                  value={cashSubWalkin.name}
                                  onChange={(e) => setCashSubWalkin({...cashSubWalkin, name: e.target.value})}
                                  className="p-2 border rounded text-sm"
                                  placeholder="Full name *"
                                  data-testid="cash-sub-walkin-name"
                                />
                                <input
                                  type="email"
                                  value={cashSubWalkin.email}
                                  onChange={(e) => setCashSubWalkin({...cashSubWalkin, email: e.target.value})}
                                  className="p-2 border rounded text-sm"
                                  placeholder="Email *"
                                  data-testid="cash-sub-walkin-email"
                                />
                                <input
                                  type="tel"
                                  value={cashSubWalkin.phone}
                                  onChange={(e) => setCashSubWalkin({...cashSubWalkin, phone: e.target.value})}
                                  className="p-2 border rounded text-sm sm:col-span-2"
                                  placeholder="Phone (optional)"
                                  data-testid="cash-sub-walkin-phone"
                                />
                              </div>
                            )}

                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Amount paid (₹)</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={cashSubAmount}
                                  onChange={(e) => setCashSubAmount(parseFloat(e.target.value) || 0)}
                                  className="w-full p-2 border rounded text-sm"
                                  data-testid="cash-sub-amount"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Method</label>
                                <select
                                  value={cashSubPaymentMethod}
                                  onChange={(e) => setCashSubPaymentMethod(e.target.value)}
                                  className="w-full p-2 border rounded text-sm"
                                  data-testid="cash-sub-method"
                                >
                                  <option value="cash">Cash</option>
                                  <option value="upi">UPI</option>
                                  <option value="bank_transfer">Bank transfer</option>
                                  <option value="free">Free / comp</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                            </div>

                            {Array.isArray(rsvpAddon?.ticket_categories) && rsvpAddon.ticket_categories.length > 0 && (
                              <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Ticket category</label>
                                <select
                                  value={cashSubCategoryId}
                                  onChange={(e) => setCashSubCategoryId(e.target.value)}
                                  className="w-full p-2 border rounded text-sm"
                                  data-testid="cash-sub-category"
                                >
                                  <option value="">— none —</option>
                                  {rsvpAddon.ticket_categories.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name} — ₹{c.member_price}{c.non_member_price != null ? ` / ₹${c.non_member_price} non-member` : ''}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            <input
                              type="text"
                              value={cashSubNotes}
                              onChange={(e) => setCashSubNotes(e.target.value)}
                              className="w-full p-2 border rounded text-sm mb-3"
                              placeholder="Notes (e.g., transaction ID, who collected)"
                              data-testid="cash-sub-notes"
                            />

                            {/* Group guests (optional) */}
                            <div className="mb-3 border-t pt-3">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-semibold text-gray-800">
                                  Group booking? <span className="text-xs font-normal text-gray-500">({1 + cashSubGuests.length} ticket{cashSubGuests.length === 0 ? '' : 's'})</span>
                                </p>
                                <button
                                  onClick={() => setCashSubGuests([...cashSubGuests, { _uid: `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: '', email: '', phone: '', is_member: false }])}
                                  className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded hover:bg-amber-200 font-medium"
                                  data-testid="cash-sub-add-guest"
                                >
                                  + Add guest
                                </button>
                              </div>
                              {cashSubGuests.map((g, idx) => (
                                <div key={g._uid || `cash-guest-${idx}`} className="bg-white border rounded p-2 mb-2" data-testid={`cash-sub-guest-${idx}`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs font-semibold text-gray-700">Guest #{idx + 1}</p>
                                    <button
                                      onClick={() => setCashSubGuests(cashSubGuests.filter((_, i) => i !== idx))}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <FiX className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-1.5">
                                    <input
                                      type="text" placeholder="Name *"
                                      value={g.name}
                                      onChange={(e) => setCashSubGuests(cashSubGuests.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                                      className="p-1.5 border rounded text-xs"
                                    />
                                    <input
                                      type="email" placeholder="Email *"
                                      value={g.email}
                                      onChange={(e) => setCashSubGuests(cashSubGuests.map((x, i) => i === idx ? { ...x, email: e.target.value } : x))}
                                      className="p-1.5 border rounded text-xs"
                                    />
                                    <input
                                      type="tel" placeholder="Phone"
                                      value={g.phone}
                                      onChange={(e) => setCashSubGuests(cashSubGuests.map((x, i) => i === idx ? { ...x, phone: e.target.value } : x))}
                                      className="p-1.5 border rounded text-xs col-span-2"
                                    />
                                  </div>
                                  <label className="flex items-center gap-1 text-xs text-gray-700 mt-1">
                                    <input
                                      type="checkbox" checked={g.is_member}
                                      onChange={(e) => setCashSubGuests(cashSubGuests.map((x, i) => i === idx ? { ...x, is_member: e.target.checked } : x))}
                                    />
                                    Library member
                                  </label>
                                </div>
                              ))}
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={_resetCashSubForm}
                                className="flex-1 py-2 text-sm border rounded text-gray-700 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleAddCashSubscriber}
                                disabled={savingCashSub}
                                className="flex-1 py-2 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 font-medium"
                                data-testid="confirm-add-cash-sub"
                              >
                                {savingCashSub ? 'Adding...' : 'Add subscriber'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {!rsvpData || rsvpData.subscribers.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500">No one has signed up for this add-on yet.</p>
                        </div>
                      ) : (
                        <>
                          {/* Headcount split */}
                          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" data-testid="rsvp-headcount-summary">
                            {(() => {
                              const groups = {};
                              let total = 0;
                              rsvpData.subscribers.forEach((s) => {
                                const qty = s.quantity || 1;
                                total += qty;
                                const key = s.ticket_category || 'General';
                                groups[key] = (groups[key] || 0) + qty;
                              });
                              return (
                                <>
                                  <span className="font-semibold text-gray-800 bg-gray-100 px-2 py-1 rounded">{total} attending</span>
                                  {Object.entries(groups).map(([name, count]) => (
                                    <span key={name} className={`px-2 py-1 rounded ${name === 'Free RSVP' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`} data-testid={`rsvp-headcount-${name.replace(/\s+/g, '-').toLowerCase()}`}>
                                      {count} {name === 'General' ? 'general' : name.toLowerCase()}{name === 'Free RSVP' ? 's' : ''}
                                    </span>
                                  ))}
                                  {rsvpAddon?.attendance_policy === 'walk_ins' && (
                                    <span className="px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid="rsvp-headcount-walkins">+ walk-ins expected</span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                                  <th className="py-2 px-3">Name</th>
                                  <th className="py-2 px-3">Contact</th>
                                  <th className="py-2 px-3">Signed up</th>
                                  <th className="py-2 px-3">Paid</th>
                                  <th className="py-2 px-3"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {rsvpData.subscribers.map((s) => (
                                  <React.Fragment key={s.subscription_id}>
                                  <tr className="border-b border-gray-100" data-testid={`rsvp-row-${s.subscription_id}`}>
                                    <td className="py-2 px-3">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-gray-800">{s.name || '—'}</p>
                                        {s.ticket_category && (
                                          <span className="text-[10px] font-semibold bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded" data-testid={`rsvp-category-${s.subscription_id}`}>
                                            {s.ticket_category}
                                          </span>
                                        )}
                                        {(s.quantity || 1) > 1 && (
                                          <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                            +{s.quantity - 1} guest{s.quantity - 1 !== 1 ? 's' : ''}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500">{s.membership_number || '—'}</p>
                                    </td>
                                    <td className="py-2 px-3">
                                      <p className="text-gray-700">{s.email}</p>
                                      {s.phone ? (
                                        <div className="flex items-center gap-2 mt-0.5">
                                          <p className="text-xs text-gray-600">{s.phone}</p>
                                          <button
                                            onClick={(e) => {
                                              const btn = e.currentTarget;
                                              const orig = btn.innerText;
                                              navigator.clipboard.writeText(s.phone).then(() => {
                                                if (!btn) return;
                                                btn.innerText = 'Copied!';
                                                setTimeout(() => { if (btn) btn.innerText = orig; }, 1200);
                                              }).catch(() => {});
                                            }}
                                            className="px-1.5 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700"
                                            data-testid={`copy-phone-${s.subscription_id}`}
                                          >
                                            Copy
                                          </button>
                                        </div>
                                      ) : (
                                        <p className="text-xs text-gray-400 mt-0.5">No phone</p>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-xs text-gray-600">
                                      {s.started_at ? new Date(s.started_at).toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—'}
                                    </td>
                                    <td className="py-2 px-3">
                                      <span className="font-medium text-purple-700">₹{s.price_paid || 0}</span>
                                      {s.payment_mode && (
                                        <p className="text-[10px] text-gray-500">{s.payment_mode}</p>
                                      )}
                                    </td>
                                    <td className="py-2 px-3">
                                      <button
                                        onClick={() => handleRemoveSubscriber(s)}
                                        disabled={removingSubId === s.subscription_id}
                                        className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                                        data-testid={`remove-sub-${s.subscription_id}`}
                                      >
                                        {removingSubId === s.subscription_id ? '...' : 'Remove'}
                                      </button>
                                    </td>
                                  </tr>
                                  {(s.guests || []).map((g, gi) => (
                                    <tr key={`${s.subscription_id}-g-${gi}`} className="bg-gray-50 border-b border-gray-100" data-testid={`rsvp-guest-${s.subscription_id}-${gi}`}>
                                      <td className="py-1.5 px-3 pl-8">
                                        <span className="text-gray-400 text-xs mr-1">↳ guest</span>
                                        <span className="text-sm text-gray-700">{g.name || '—'}</span>
                                      </td>
                                      <td className="py-1.5 px-3 text-xs text-gray-600">
                                        {g.email || '—'}
                                        {g.phone ? <span className="ml-2">{g.phone}</span> : null}
                                      </td>
                                      <td className="py-1.5 px-3"></td>
                                      <td className="py-1.5 px-3 text-xs text-gray-500">
                                        {g.is_member ? 'Member' : 'Non-member'}
                                      </td>
                                      <td className="py-1.5 px-3"></td>
                                    </tr>
                                  ))}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                            <button
                              onClick={printCheckinSheet}
                              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 font-medium text-sm"
                              data-testid="print-checkin-btn"
                            >
                              🖨 Print check-in sheet
                            </button>
                            <button
                              onClick={handleSendFollowup}
                              disabled={sendingFollowup}
                              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium text-sm"
                              data-testid="send-followup-btn"
                            >
                              {sendingFollowup ? 'Sending...' : '💌 Send thank-you + what\'s next'}
                            </button>
                            <button
                              onClick={handleSendAddonReminder}
                              disabled={sendingReminder}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
                              data-testid="send-reminder-btn"
                            >
                              {sendingReminder ? 'Sending...' : `📧 Send reminder to all ${rsvpData.subscribers.length}`}
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Backup Tab */}
        {activeTab === 'backup' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Data Backup</h2>
              <button
                onClick={fetchBackupInfo}
                className="text-amber-600 hover:text-amber-700 font-medium"
              >
                Refresh
              </button>
            </div>

            {loadingBackup ? (
              <p className="text-center text-gray-600 py-8">Loading backup information...</p>
            ) : !backupInfo ? (
              <p className="text-center text-gray-600 py-8">Unable to load backup information</p>
            ) : (
              <div className="space-y-6">
                {/* Summary */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <h3 className="font-semibold text-amber-800 mb-4">Database Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-amber-600">{backupInfo.counts?.books || 0}</p>
                      <p className="text-sm text-gray-600">Books</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-blue-600">{backupInfo.counts?.users || 0}</p>
                      <p className="text-sm text-gray-600">Users</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-green-600">{backupInfo.counts?.holds || 0}</p>
                      <p className="text-sm text-gray-600">Holds</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-purple-600">{backupInfo.counts?.reviews || 0}</p>
                      <p className="text-sm text-gray-600">Reviews</p>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                      <p className="text-3xl font-bold text-gray-600">{backupInfo.counts?.payments || 0}</p>
                      <p className="text-sm text-gray-600">Payments</p>
                    </div>
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-800">Download Backups</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Download your data as CSV files. We recommend taking regular backups to protect your data.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <button
                      onClick={() => handleDownloadBackup('books')}
                      disabled={downloadingBackup === 'books'}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-amber-500 hover:bg-amber-50 transition-colors disabled:opacity-50"
                      data-testid="download-books-backup"
                    >
                      <div className="flex items-center space-x-3">
                        <FiBook className="w-6 h-6 text-amber-600" />
                        <div className="text-left">
                          <p className="font-semibold text-gray-800">Books Catalog</p>
                          <p className="text-sm text-gray-500">{backupInfo.counts?.books || 0} records</p>
                        </div>
                      </div>
                      <FiDownload className={`w-5 h-5 text-gray-500 ${downloadingBackup === 'books' ? 'animate-bounce' : ''}`} />
                    </button>

                    <button
                      onClick={() => handleDownloadBackup('users')}
                      disabled={downloadingBackup === 'users'}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50"
                      data-testid="download-users-backup"
                    >
                      <div className="flex items-center space-x-3">
                        <FiUsers className="w-6 h-6 text-blue-600" />
                        <div className="text-left">
                          <p className="font-semibold text-gray-800">Users</p>
                          <p className="text-sm text-gray-500">{backupInfo.counts?.users || 0} records</p>
                        </div>
                      </div>
                      <FiDownload className={`w-5 h-5 text-gray-500 ${downloadingBackup === 'users' ? 'animate-bounce' : ''}`} />
                    </button>

                    <button
                      onClick={() => handleDownloadBackup('holds')}
                      disabled={downloadingBackup === 'holds'}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors disabled:opacity-50"
                      data-testid="download-holds-backup"
                    >
                      <div className="flex items-center space-x-3">
                        <FiClock className="w-6 h-6 text-green-600" />
                        <div className="text-left">
                          <p className="font-semibold text-gray-800">Holds & Borrowing History</p>
                          <p className="text-sm text-gray-500">{backupInfo.counts?.holds || 0} records</p>
                        </div>
                      </div>
                      <FiDownload className={`w-5 h-5 text-gray-500 ${downloadingBackup === 'holds' ? 'animate-bounce' : ''}`} />
                    </button>

                    <button
                      onClick={() => handleDownloadBackup('reviews')}
                      disabled={downloadingBackup === 'reviews'}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors disabled:opacity-50"
                      data-testid="download-reviews-backup"
                    >
                      <div className="flex items-center space-x-3">
                        <FiMessageSquare className="w-6 h-6 text-purple-600" />
                        <div className="text-left">
                          <p className="font-semibold text-gray-800">Reviews</p>
                          <p className="text-sm text-gray-500">{backupInfo.counts?.reviews || 0} records</p>
                        </div>
                      </div>
                      <FiDownload className={`w-5 h-5 text-gray-500 ${downloadingBackup === 'reviews' ? 'animate-bounce' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Last Updated */}
                <div className="text-center text-sm text-gray-500 pt-4 border-t">
                  Data as of: {new Date(backupInfo.timestamp).toLocaleString()}
                </div>

                {/* Remove Test Accounts */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-semibold text-gray-800 mb-3">Maintenance</h3>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Remove test accounts (test_member@test.com and test_staff@test.com)?')) return;
                      try {
                        const response = await axios.delete(`${API}/admin/remove-test-accounts`, { headers: getAuthHeader() });
                        alert(response.data.message);
                        fetchBackupInfo();
                      } catch (error) {
                        alert(error.response?.data?.detail || 'Failed to remove test accounts');
                      }
                    }}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                    data-testid="remove-test-accounts-button"
                  >
                    Remove Test Accounts
                  </button>
                </div>

                {/* Subscription Reminders */}
                <div className="mt-6 pt-6 border-t">
                  <h3 className="font-semibold text-gray-800 mb-3">📧 Subscription Reminders</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Send reminder emails to members who signed up but haven't activated their subscription.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/admin/inactive-subscribers?days=7`, { headers: getAuthHeader() });
                          const count = response.data.count;
                          if (count === 0) {
                            alert('No inactive members found (7+ days without subscription)');
                            return;
                          }
                          if (!window.confirm(`Found ${count} members who joined 7+ days ago without subscribing.\n\nSend subscription reminder emails to all of them?`)) return;
                          
                          const sendResponse = await axios.post(`${API}/admin/send-subscription-reminders?days=7`, {}, { headers: getAuthHeader() });
                          alert(`✅ ${sendResponse.data.message}`);
                        } catch (error) {
                          alert(error.response?.data?.detail || 'Failed to send reminders');
                        }
                      }}
                      className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
                      data-testid="send-subscription-reminders"
                    >
                      📚 Send Subscription Reminders (7+ days inactive)
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/admin/inactive-subscribers?days=7`, { headers: getAuthHeader() });
                          const members = response.data.members || [];
                          if (members.length === 0) {
                            alert('No inactive members found');
                            return;
                          }
                          const list = members.slice(0, 10).map(m => `• ${m.name} (${m.email})`).join('\n');
                          const more = members.length > 10 ? `\n... and ${members.length - 10} more` : '';
                          alert(`Inactive Members (7+ days):\n\n${list}${more}\n\nTotal: ${members.length}`);
                        } catch (error) {
                          alert(error.response?.data?.detail || 'Failed to fetch inactive members');
                        }
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      data-testid="view-inactive-subscribers"
                    >
                      👁️ View Inactive Members
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Member Actions Tab */}
        {activeTab === 'member-actions' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Checkout / Hold for Member</h2>
              <p className="text-gray-600 mt-1">Place a hold or check out a book directly for a member</p>
            </div>

            <div className="space-y-6">
              {/* Member Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Member <span className="text-gray-400 font-normal">(by name, email, or membership number)</span>
                </label>
                {selectedMember ? (
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <p className="font-semibold text-gray-800">{selectedMember.name}</p>
                      <p className="text-sm text-gray-600">{selectedMember.email}</p>
                      <p className="text-xs text-gray-500">#{selectedMember.membership_number} {selectedMember.phone && `• ${selectedMember.phone}`}</p>
                      {selectedMember.child_membership_status === 'active' && (
                        <label className="mt-2 flex items-center gap-2 text-sm text-purple-800 bg-purple-50 border border-purple-200 rounded px-2.5 py-1.5 cursor-pointer w-fit" data-testid="checkout-for-child-toggle">
                          <input type="checkbox" checked={checkoutForChild} onChange={(e) => setCheckoutForChild(e.target.checked)} className="w-4 h-4" data-testid="checkout-for-child-checkbox" />
                          <span>Checkout / hold on children&apos;s membership{selectedMember.child_name ? ` (${selectedMember.child_name})` : ''}</span>
                        </label>
                      )}
                      <button onClick={() => openPickupModal(selectedMember)} className="mt-2 px-3 py-1 bg-teal-600 text-white text-xs rounded hover:bg-teal-700" data-testid="checkout-schedule-pickup-btn">📦 Schedule Thursday pickup</button>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedMember(null);
                        setMemberSearchQuery('');
                        setMemberActionEmail('');
                        setCheckoutForChild(false);
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={memberSearchQuery}
                      onChange={(e) => {
                        setMemberSearchQuery(e.target.value);
                        searchMembers(e.target.value);
                      }}
                      placeholder="Type name, email, or membership number..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      data-testid="member-search-input"
                    />
                    {searchingMembers && (
                      <div className="absolute right-3 top-10 text-gray-400">
                        <div className="animate-spin h-5 w-5 border-2 border-amber-500 rounded-full border-t-transparent"></div>
                      </div>
                    )}
                    {/* Member Search Results Dropdown */}
                    {memberSearchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {memberSearchResults.map((member) => (
                          <div
                            key={member.id}
                            onClick={() => {
                              setSelectedMember(member);
                              setMemberActionEmail(member.email);
                              setMemberSearchQuery('');
                              setMemberSearchResults([]);
                              setCheckoutForChild(false);
                            }}
                            className="p-3 hover:bg-amber-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            data-testid={`member-result-${member.id}`}
                          >
                            <p className="font-medium text-gray-800">
                              {member.name}
                              {member.role === 'staff' && <span className="ml-2 text-[11px] font-bold bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5">STAFF</span>}
                            </p>
                            <p className="text-sm text-gray-600">{member.email}</p>
                            <p className="text-xs text-gray-500">#{member.membership_number}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Book Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search for Book
                </label>
                <input
                  type="text"
                  value={memberActionBookSearch}
                  onChange={(e) => {
                    setMemberActionBookSearch(e.target.value);
                    searchBooksForMemberAction(e.target.value);
                  }}
                  placeholder="Search by title or author..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="member-action-book-search"
                />
                
                {/* Search Results */}
                {memberActionBookResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                    {memberActionBookResults.map((book) => (
                      <div
                        key={book.id}
                        onClick={() => {
                          setSelectedBooksForAction(prev => prev.some(b => b.id === book.id) ? prev : [...prev, book]);
                          setMemberActionBookSearch('');
                          setMemberActionBookResults([]);
                        }}
                        className="flex items-center p-3 hover:bg-amber-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        data-testid={`member-action-book-result-${book.id}`}
                      >
                        {book.cover_image_url ? (
                          <img src={book.cover_image_url} alt={book.title} className="w-10 h-14 object-cover rounded mr-3" />
                        ) : (
                          <div className="w-10 h-14 bg-amber-100 rounded mr-3 flex items-center justify-center">
                            <FiBook className="w-5 h-5 text-amber-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{book.title}</p>
                          <p className="text-sm text-gray-500">{book.authors?.join(', ')}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            book.available_copies > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {book.available_copies > 0 ? `${book.available_copies} available` : 'Not available'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searchingMemberBooks && (
                  <p className="mt-2 text-sm text-amber-600">Searching...</p>
                )}
              </div>

              {/* Selected Books basket */}
              {selectedBooksForAction.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="selected-books-basket">
                  <p className="text-sm font-semibold text-gray-700 mb-2">{selectedBooksForAction.length} book{selectedBooksForAction.length === 1 ? '' : 's'} selected — search above to add more</p>
                  <div className="space-y-2">
                    {selectedBooksForAction.map((book) => (
                      <div key={book.id} className="flex items-center justify-between bg-white border border-amber-100 rounded p-2">
                        <div className="flex items-center min-w-0">
                          {book.cover_image_url ? (
                            <img src={book.cover_image_url} alt={book.title} className="w-8 h-11 object-cover rounded mr-3 flex-shrink-0" />
                          ) : (
                            <div className="w-8 h-11 bg-amber-200 rounded mr-3 flex items-center justify-center flex-shrink-0">
                              <FiBook className="w-4 h-4 text-amber-600" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-800 text-sm truncate">{book.title}</p>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              book.available_copies > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {book.available_copies > 0 ? `${book.available_copies} available` : 'Not available'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedBooksForAction(prev => prev.filter(b => b.id !== book.id))}
                          className="text-gray-500 hover:text-red-600 ml-2 flex-shrink-0"
                          data-testid={`remove-selected-book-${book.id}`}
                        >
                          <FiX className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Requested Pickup Date (for Place Hold) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Requested Pickup Date <span className="text-gray-400 font-normal">(optional, for Place Hold only)</span>
                </label>
                <input
                  type="date"
                  value={memberHoldRequestedDate}
                  onChange={(e) => setMemberHoldRequestedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="member-hold-requested-date"
                />
                {memberHoldRequestedDate && (
                  <p className="mt-1 text-sm text-blue-600">
                    Member wants to pick up on: {new Date(memberHoldRequestedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={handlePlaceHoldForMember}
                  disabled={processingMemberAction || (!selectedMember && !memberActionEmail) || selectedBooksForAction.length === 0}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="place-hold-for-member"
                >
                  {processingMemberAction ? 'Processing...' : `Place Hold${selectedBooksForAction.length > 1 ? ` (${selectedBooksForAction.length})` : ''}`}
                </button>
                <button
                  onClick={handleCheckoutForMember}
                  disabled={processingMemberAction || (!selectedMember && !memberActionEmail) || selectedBooksForAction.length === 0}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="checkout-for-member"
                >
                  {processingMemberAction ? 'Processing...' : `Checkout Now${selectedBooksForAction.length > 1 ? ` (${selectedBooksForAction.length})` : ''}`}
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <p className="font-medium mb-2">Notes:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Place Hold:</strong> Adds book(s) to member&apos;s &quot;Next Up&quot; queue</li>
                  <li><strong>Checkout Now:</strong> Immediately checks out all selected books (21 day loan period)</li>
                  <li>Search and tap books repeatedly to build a stack — then check them all out in one go.</li>
                  <li>Members can have max 5 active holds/borrows. Additional holds go to queue.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Recommendation Requests Tab */}
        {activeTab === 'recommendations' && (
          <div className="bg-white rounded-xl shadow-lg p-8" data-testid="recommendations-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Book Recommendation Requests</h2>
              <span className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg font-semibold">
                {recommendationRequests.length} pending {recommendationRequests.length === 1 ? 'request' : 'requests'}
              </span>
            </div>

            {loadingRecommendations ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              </div>
            ) : recommendationRequests.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <FiHeart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No pending recommendation requests</p>
                <p className="text-sm mt-2">When members ask for book suggestions, they&apos;ll appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recommendationRequests.map((request) => (
                  <div key={request.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-800">{request.user_name}</h3>
                        <p className="text-sm text-gray-500">{request.user_email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Requested: {new Date(request.created_at).toLocaleDateString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedRequest(selectedRequest?.id === request.id ? null : request);
                            setSelectedRecommendations([]);
                            setRecommendationBookSearch('');
                          }}
                          className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          {selectedRequest?.id === request.id ? 'Cancel' : 'Respond'}
                        </button>
                        <button
                          onClick={() => handleDismissRequest(request.id)}
                          className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>

                    {/* Member's Preferences */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-sm text-gray-700">
                        <strong>Their request:</strong> &quot;{request.preferences}&quot;
                      </p>
                    </div>

                    {/* Reading History */}
                    {request.books_read && request.books_read.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 mb-2">📚 Their reading history ({request.books_read.length} books):</p>
                        <div className="flex flex-wrap gap-2">
                          {request.books_read.slice(0, 8).map((book, idx) => (
                            <span key={book.id || book.book_id || `${request.id || ''}-rb-${idx}-${String(book.title).slice(0, 16)}`} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                              {book.title}
                            </span>
                          ))}
                          {request.books_read.length > 8 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                              +{request.books_read.length - 8} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Response Panel */}
                    {selectedRequest?.id === request.id && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-medium text-gray-800 mb-3">Select books to recommend:</h4>
                        
                        {/* Book Search */}
                        <div className="relative mb-3">
                          <input
                            type="text"
                            value={recommendationBookSearch}
                            onChange={(e) => {
                              setRecommendationBookSearch(e.target.value);
                              searchBooksForRecommendation(e.target.value);
                            }}
                            placeholder="Search for books to recommend..."
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          />
                          {recommendationBookResults.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {recommendationBookResults.map((book) => (
                                <div
                                  key={book.id}
                                  onClick={() => {
                                    if (!selectedRecommendations.find(b => b.id === book.id)) {
                                      setSelectedRecommendations([...selectedRecommendations, book]);
                                    }
                                    setRecommendationBookSearch('');
                                    setRecommendationBookResults([]);
                                  }}
                                  className="p-2 hover:bg-amber-50 cursor-pointer flex items-center gap-2"
                                >
                                  {book.cover_image_url && (
                                    <img src={book.cover_image_url} alt="" className="w-8 h-10 object-cover rounded" />
                                  )}
                                  <div>
                                    <p className="font-medium text-sm">{book.title}</p>
                                    <p className="text-xs text-gray-500">{book.authors?.join(', ')}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Selected Recommendations */}
                        {selectedRecommendations.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-600 mb-2">Selected ({selectedRecommendations.length}):</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedRecommendations.map((book) => (
                                <span key={book.id} className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-sm">
                                  {book.title}
                                  <button
                                    onClick={() => setSelectedRecommendations(selectedRecommendations.filter(b => b.id !== book.id))}
                                    className="ml-1 text-amber-600 hover:text-amber-800"
                                  >
                                    <FiX className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <button
                          onClick={() => handleSendRecommendations(request.id)}
                          disabled={selectedRecommendations.length === 0}
                          className="w-full py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Send {selectedRecommendations.length} Recommendation{selectedRecommendations.length !== 1 ? 's' : ''}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Coming Soon Calendar Tab */}
        {activeTab === 'coming-soon-calendar' && (
          <div className="bg-white rounded-xl shadow-lg p-8" data-testid="coming-soon-calendar-content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Book Release Calendar</h2>
              <div className="flex gap-3">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-semibold">
                  ✓ {calendarData.launched_count || 0} launched
                </span>
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-sm font-semibold">
                  📅 {calendarData.upcoming_count || 0} upcoming
                </span>
              </div>
            </div>

            {loadingCalendar ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
              </div>
            ) : calendarData.total_books === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <FiCalendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">No books with release dates</p>
                <p className="text-sm mt-2">Add books with an &quot;Available From&quot; date to see them here</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Calendar */}
                <div className="flex flex-col items-center">
                  <Calendar
                    mode="single"
                    selected={selectedCalendarDate}
                    onSelect={(date) => {
                      if (date) {
                        const dateStr = date.toISOString().split('T')[0];
                        setSelectedCalendarDate(calendarData.dates[dateStr] ? date : null);
                      } else {
                        setSelectedCalendarDate(null);
                      }
                    }}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    className="rounded-lg border shadow-sm"
                    modifiers={{
                      hasUpcoming: (date) => {
                        const dateStr = date.toISOString().split('T')[0];
                        const books = calendarData.dates[dateStr];
                        return books && books.some(b => !b.is_launched);
                      },
                      hasLaunched: (date) => {
                        const dateStr = date.toISOString().split('T')[0];
                        const books = calendarData.dates[dateStr];
                        return books && books.every(b => b.is_launched);
                      }
                    }}
                    modifiersStyles={{
                      hasUpcoming: {
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        fontWeight: 'bold'
                      },
                      hasLaunched: {
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        fontWeight: 'bold'
                      }
                    }}
                    components={{
                      DayContent: ({ date }) => {
                        const dateStr = date.toISOString().split('T')[0];
                        const books = calendarData.dates[dateStr] || [];
                        const bookCount = books.length;
                        const allLaunched = books.length > 0 && books.every(b => b.is_launched);
                        return (
                          <div className="relative w-full h-full flex flex-col items-center justify-center">
                            <span>{date.getDate()}</span>
                            {bookCount > 0 && (
                              <span className={`absolute -bottom-1 text-[10px] text-white rounded-full w-4 h-4 flex items-center justify-center ${
                                allLaunched ? 'bg-green-600' : 'bg-amber-600'
                              }`}>
                                {bookCount}
                              </span>
                            )}
                          </div>
                        );
                      }
                    }}
                  />
                  <div className="mt-4 flex gap-4 text-sm text-gray-500">
                    <span>
                      <span className="inline-block w-4 h-4 bg-amber-100 rounded mr-1 align-middle"></span>
                      Upcoming
                    </span>
                    <span>
                      <span className="inline-block w-4 h-4 bg-green-100 rounded mr-1 align-middle"></span>
                      Launched
                    </span>
                  </div>
                </div>

                {/* Book List for Selected Date */}
                <div>
                  {selectedCalendarDate ? (
                    <div>
                      {(() => {
                        const dateStr = selectedCalendarDate.toISOString().split('T')[0];
                        const books = calendarData.dates[dateStr] || [];
                        const isLaunched = books.length > 0 && books[0].is_launched;
                        return (
                          <>
                            <h3 className="text-lg font-semibold mb-2 text-gray-800">
                              {isLaunched ? '✓ Launched on' : '📅 Releasing on'} {selectedCalendarDate.toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </h3>
                            {isLaunched && (
                              <p className="text-sm text-green-600 mb-4">These books are now available for borrowing!</p>
                            )}
                          </>
                        );
                      })()}
                      <div className="space-y-4">
                        {calendarData.dates[selectedCalendarDate.toISOString().split('T')[0]]?.map((book) => (
                          <div 
                            key={book.id} 
                            className={`flex items-center gap-4 p-4 rounded-lg transition-colors cursor-pointer ${
                              book.is_launched 
                                ? 'bg-green-50 hover:bg-green-100 border border-green-200' 
                                : 'bg-gray-50 hover:bg-amber-50'
                            }`}
                            onClick={() => navigate(`/book/${book.id}`)}
                            data-testid={`calendar-book-${book.id}`}
                          >
                            {book.cover_image_url ? (
                              <img 
                                src={book.cover_image_url} 
                                alt={book.title}
                                className="w-16 h-20 object-cover rounded shadow"
                              />
                            ) : (
                              <div className="w-16 h-20 bg-gray-200 rounded flex items-center justify-center">
                                <FiBook className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-800 truncate">{book.title}</h4>
                                {book.is_launched && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">
                                    Available
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 truncate">
                                {book.authors?.join(', ') || 'Unknown Author'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16 text-gray-500 bg-gray-50 rounded-lg">
                      <FiCalendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a highlighted date to see books</p>
                    </div>
                  )}

                  {/* All Releases Summary */}
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">All Releases</h3>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {Object.entries(calendarData.dates)
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([date, books]) => {
                          const isLaunched = books.every(b => b.is_launched);
                          return (
                            <div 
                              key={date}
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                                isLaunched 
                                  ? 'bg-green-50 hover:bg-green-100' 
                                  : 'bg-gray-50 hover:bg-amber-50'
                              }`}
                              onClick={() => {
                                setSelectedCalendarDate(new Date(date + 'T00:00:00'));
                                setCalendarMonth(new Date(date + 'T00:00:00'));
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`text-sm ${isLaunched ? 'text-green-600' : 'text-amber-600'}`}>
                                  {isLaunched ? '✓' : '📅'}
                                </span>
                                <span className="font-medium text-gray-700">
                                  {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-sm font-semibold ${
                                isLaunched 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {books.length} {books.length === 1 ? 'book' : 'books'}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Edit Hold Modal */}
      {editingHold && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">Edit Hold</h2>
              <button
                onClick={() => setEditingHold(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 font-medium">{editingHold.book?.title}</p>
                <p className="text-sm text-gray-500">Member: {editingHold.user?.name || editingHold.user?.email}{editingHold.is_child_hold ? ` (${editingHold.child_name || 'Child'} — Children membership)` : ''}</p>
                <p className="text-sm text-gray-500">Status: {editingHold.status}</p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Pickup Date
                </label>
                <input
                  type="date"
                  value={editHoldData.preferred_date}
                  onChange={(e) => setEditHoldData({...editHoldData, preferred_date: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Option
                </label>
                <select
                  value={editHoldData.delivery_option}
                  onChange={(e) => setEditHoldData({...editHoldData, delivery_option: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                >
                  <option value="self_pickup">Self Pickup</option>
                  <option value="paid_delivery">Paid Delivery</option>
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleSaveHoldEdit}
                  disabled={savingHold}
                  className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {savingHold ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => setEditingHold(null)}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deliveryModalHold && (
        <BatchDeliveryModal
          hold={deliveryModalHold}
          getAuthHeader={getAuthHeader}
          onClose={() => setDeliveryModalHold(null)}
          onSuccess={(res) => {
            const msg = res?.message || 'Holds delivered';
            const bal = typeof res?.new_balance === 'number'
              ? ` New balance: ₹${res.new_balance.toFixed(2)}.`
              : '';
            alert(`${msg}.${bal}`);
            setDeliveryModalHold(null);
            fetchAllHolds();
            if (userDetails?.user?.id) fetchUserDetails(userDetails.user.id);
          }}
        />
      )}
    </Layout>
  );
}

export default AdminDashboard;